<template>
  <!-- eslint-disable vue/no-use-v-if-with-v-for,vue/no-confusing-v-for-v-if -->
  <div
    class="markups-popup"
    :class="{ fullscreen: isFullscreen, minimized: isMinimized, hidden: isUndefined }">
    <markups-message-popup
      :type="popupMessageType"
      :message="popupMessageText"
      :callback="popupMessageCallback"></markups-message-popup>
    <stamps-popup @createstamp="onCreateStamp($event)"></stamps-popup>
    <symbols-popup @create-test-blind="onCreateSymbol($event)"></symbols-popup>
    <test-blind-popup @create-test-blind="onCreateTestBlind($event)"></test-blind-popup>
    <link-class-popup
      @create-markup-link="onCreateMarkupLink($event)"></link-class-popup>

    <!-- Header Bar -->
    <v-sheet v-if="!isFullscreen" class="markup-header px-4 py-2 d-flex align-center" outlined @mousedown="onHeaderMouseDown($event)" color="bg1">
      <p class="font-weight-bold h6 mb-0">Create/Edit Markup</p>
      <v-spacer></v-spacer>
      <v-sheet class="markup-header-btn-group" color="transparent">
        <v-btn icon small @click="minimize()" :class="{ 'hidden': isMinimized }">
          <v-icon size="18">mdi-window-minimize</v-icon>
        </v-btn>
        <v-btn icon small @click="hidePopup()">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </v-sheet>
    </v-sheet>

    <!-- Top Toolbar Component -->
    <markup-toolbar
      v-if="!isFlutterRunning && areToolsShown"
      :active-tool="activeTool"
      :is-undo-disabled="isUndoStackEmpty"
      :is-redo-disabled="isRedoStackEmpty"
      :current-style="currentMarkupsStyle"
      @tool-clicked="onMarkupToolClicked($event)"
      @undo="onUndo()"
      @redo="onRedo()"
      @style-changed="onStyleChanged($event)">
      <!-- Import Links Inline Panel -->
      <template v-slot:import-panel>
        <import-links-inline-panel
          :viewer="markupViewer"
          @import-links="onImportLinks($event)"
          @discard="onImportDiscard">
        </import-links-inline-panel>
      </template>
    </markup-toolbar>

    <!-- Main Content Area -->
    <div class="markup-main-content disable-select">
      <div class="toolbar-btn show-toolbar-btn hidden" @mousedown="onShowToolbar($event)">
        <i class="fas fa-eye vertical-center"></i>
        <span class="vertical-center">Show toolbar</span>
      </div>
      <div class="toolbar-btn close-btn hidden" @mousedown="hidePopup()">
        <span class="vertical-center">Close</span>
      </div>

      <!-- Left Side Panels -->
      <div class="markup-left-panels">
        <!-- Collapsible Panels Container -->
        <div class="collapsible-panels-container">
          <!-- Links Panel -->
          <div class="collapsible-panel" :class="{ 'panel-expanded-full': isPanelExpandedFull('links') }">
            <div class="panel-header" @click="togglePanel('links')">
              <v-icon small class="mr-2">mdi-link</v-icon>
              <span class="panel-title">Links</span>
              <v-badge
                v-if="linksCount > 0"
                :content="linksCount"
                color="primary"
                inline
                class="ml-2">
              </v-badge>
              <v-spacer></v-spacer>
              <v-icon small>{{ isPanelExpanded('links') ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="isPanelExpanded('links')" class="panel-body">
                <div class="panel-content-scroll">
                  <markup-links-panel
                    ref="markupLinksPanel"
                    v-if="isPanelExpanded('links')"
                    :page-markup="activeMarkupPage"
                    :viewer="markupViewer"
                    @link-clicked="onLinksPanelLinkClicked($event)"
                    @link-selected="onLinksPanelLinkSelected($event)"
                    @visibility-changed="onLinkVisibilityChanged($event)"
                    @unlink="onDeleteMarkupLink($event)"
                    @reLink="onRestoreMarkupLink($event)">
                  </markup-links-panel>
                </div>
              </div>
            </v-expand-transition>
          </div>

          <!-- Comments Panel -->
          <div class="collapsible-panel" :class="{ 'panel-expanded-full': isPanelExpandedFull('comments') }">
            <div class="panel-header" @click="togglePanel('comments')">
              <v-icon small class="mr-2">mdi-comment-text-outline</v-icon>
              <span class="panel-title">Comments</span>
              <v-badge
                v-if="commentsCount > 0"
                :content="commentsCount"
                color="primary"
                inline
                class="ml-2">
              </v-badge>
              <v-spacer></v-spacer>
              <v-icon small>{{ isPanelExpanded('comments') ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </div>
            <v-expand-transition>
              <div v-show="isPanelExpanded('comments')" class="panel-body">
                <div class="panel-content-scroll">
                  <markups-comments-inline
                    v-if="isPanelExpanded('comments')"
                    :active_page="activeMarkupPage"
                    @save_new_markup="saveNewMarkup">
                  </markups-comments-inline>
                </div>
              </div>
            </v-expand-transition>
          </div>
        </div>

      </div>

      <!-- Viewer Area -->
      <div id="markup-viewer" :style="{ width: viewerWidth }">
        <loading-popup
          :show="isMarkupsSaving && !isFlutterRunning"
          :text="'Your markups are being saved.'"></loading-popup>
        <div v-show="isMarkupSelected && $has(permissions.Markups.Delete)" class="markup-delete-floating-button">
          <div class="fa-lg fas fa-times" @click="onMarkupDelete()"></div>
        </div>
        <div v-show="drawingLinkInfo.isDrawing" class="markup-link-draw-highlight"></div>
        <link-preview-popup
          v-show="previewingLink"
          ref="linkpreview"
          :id="previewingLink ? previewingLink.id : null"
          :link="previewingLink"
          :viewer="markupViewer"
          :has_selector_box="true"
          @onlinkopen="onMarkupLinkOpen($event)"
          @onlinkpreviewclick="onMarkupLinkClick($event, previewingLink)"
          @ondeletelink="onDeleteMarkupLink($event)">
        </link-preview-popup>
      </div>

      <div class="show-tools-btn" v-if="!areToolsShown" @click="areToolsShown = true">
        Show Tools
      </div>
    </div>

    <!-- Bottom Bar with Thumbnails, Page Navigator and Actions -->
    <div class="markup-bottom-section" v-if="!isFlutterRunning">
      <!-- Thumbnail Grid (shown when toggle is active) -->
      <v-expand-transition>
        <div v-show="showThumbnailGrid" class="thumbnail-grid-container">
          <thumbnail-list
            ref="thumbnaillist"
            :isActiveTool="showThumbnailGrid"
            :viewer="markupViewer"
            :compact="true">
          </thumbnail-list>
        </div>
      </v-expand-transition>

      <!-- Page Navigator -->
      <markup-page-navigator
        :current-page="currentPageNumber"
        :total-pages="totalPages || 1"
        :show-thumbnails="showThumbnailGrid"
        @page-change="onPageChange($event)"
        @toggle-thumbnails="toggleThumbnailGrid">
      </markup-page-navigator>

      <!-- Action Bar -->
      <v-sheet class="markup-bottom-bar" color="bg1">
        <v-spacer></v-spacer>
        <div class="bottom-bar-actions">
          <v-btn
            text
            small
            color="error"
            class="action-btn"
            @click="onMarkupToolClicked({ id: 'discard' })">
            <v-icon left small>mdi-close</v-icon>
            Discard
          </v-btn>
          <v-btn
            
            color="primary"
            small
            class="action-btn save-btn"
            v-has.any="[permissions.Markups.Update,permissions.Markups.Create]"
            @click="onMarkupToolClicked({ id: 'save' })">
            <v-icon left small>mdi-content-save</v-icon>
            Save
          </v-btn>
        </div>
      </v-sheet>
    </div>
  </div>
</template>

<script>
  /* eslint-disable no-unused-vars */
  import { ForgeEquipment, ForgePipeline } from '@/scripts/js/forgeModels';
  const THREE = window.THREE;
  const Autodesk = window.Autodesk;
  import $ from 'jquery';
  import { isPermitModule, isParentOrigin, ForgeMessage } from '../../utils/forgeMessageUtils';
  import {
    onMarkupViewerLoaded,
    onMarkupLinkClickedFlutter,
    onEditModeLeaveFlutter,
    getMarkupsComponent,
    pdfViewerOpenPageByNumber,
    drawImageOnPlane,
    onHistoryChangedFlutter,
    updateMarkupsOnViewer,
    onSaveFlutter,
  } from '../../scripts/js/forgeviewer';
  import { ForgeAttachment, ForgeMarkupLink, ForgePageMarkup } from '../../scripts/js/forgeModels';
  import { getAttachmentByReference, postAttachment, getAttachment } from '../../utils/mongoUtils';
  import {
    rotateViewerCamera,
    rotateViewerMarkups,
    getViewerCurrentPdfPagesCount,
    unloadViewerAllModels,
  } from '../../utils/forgeUtils';
  import {
    getPipingDrawingLinkUrl,
    addNewBlindMarkupsToOrigin,
    getAllPipingDocuments,
  } from '../../utils/testBlindUtils';
  import {
    PopupStatus,
    saveAttachmentToDatabase,
    exportMarkupImageWithModel,
    isScriptLoaded,
    loadScript,
    addMarkupsForPrint,
    exportMarkupImage,
    deletePrintMarkups,
    pad,
    _addEvent,
  } from '../../scripts/js/forgeService';
  import appConfig from '../../../appConfig';
  import { getEmptyFileUrl } from '@/utils/attachmentsAPI';
  import { $getAuthHeaders } from '@/utils/index.js';
  import { log, warn, error } from '@/utils/debugLogger';
  import { fetchDocumentAsObjectUrl } from '@/utils/documentProxyUtils';
  import { LINK_TYPE_COLORS } from '@/constants/linkTypeColors';
  import { createAnnotationBoxElement, updateBoxPositions, cleanupBoxElements } from '@/utils/annotationBoxUtils';
  import permissions from '@Share/config/permissions';
  import { $has } from '@/utils';
  // var filesFolderPath = (apiUrl ? apiUrl : '../../') + 'files/';

  export default {
    name: 'markups-popup',
    components: {
      'markup-toolbar': () => import('./MarkupToolbar.vue'),
      'markup-page-navigator': () => import('./MarkupPageNavigator.vue'),
      'markup-links-panel': () => import('./MarkupLinksPanel.vue'),
      'stamps-popup': () => import('./stampsPopup.vue'),
      'symbols-popup': () => import('./symbolsPopup.vue'),
      'test-blind-popup': () => import('./testBlindPopup.vue'),
      'link-class-popup': () => import('./linkClassPopup.vue'),
      'import-links-inline-panel': () => import('./ImportLinksInlinePanel.vue'),
      'link-preview-popup': () => import('./linkPreviewPopup.vue'),
      'markups-message-popup': () => import('./markupsMessagePopup.vue'),
      'markups-comments-inline': () => import('./markupsCommentsInline.vue'),
      'thumbnail-list': () => import('./thumbnailList.vue'),
      LoadingPopup: () => import('@/components/common/loadingPopup.vue'),
    },
    data() {
      return {
        permissions,
        // filesFolderPath: filesFolderPath,
        toolbarVisible: false,
        attachment: null,
        activeMarkupPage: null,
        markupViewer: null,
        autodeskMarkupsExtension: null,
        stampsExtension: null,
        planeMesh: null,
        markupScene: 'markup-scene',
        markupLayer: 'Layer_1',
        color: '#ff0000',
        currentPageNumber: 1,
        currentPageId: '',
        doc: null,
        viewables: [],
        isCropping: false,
        currentCropBox: null,
        popupMessageType: '',
        popupMessageText: '',
        popupMessageCallback: '',
        pipingDocuments: {},
        selectedLinkClass: [],
        previewingLink: null,
        cameraPosition: -1,
        cameraMaxZ: 20,
        isMarkupSelected: false,
        selectedMarkup: null,
        isMarkupsSaving: false,
        isPermitModule: isPermitModule(),
        expandedPanels: ['comments'],
        showThumbnailGrid: false,
        movingPopupInfo: {
          isMoving: false,
          x: 0,
          y: 0,
          left: 0,
          top: 0,
        },
        drawingLinkInfo: {
          isDrawing: false,
          x1: 0,
          y1: 0,
          x2: 0,
          y2: 0,
        },
        importedLinkIds: [], // Track imported links for potential rollback
        importBoxElements: [], // Track V7 annotation box DOM elements for camera updates
        topToolbarItems: [
          {
            id: 'text',
            name: 'Text',
            icon: 'mdi-format-text',
            disabled: false,
          },
          {
            id: 'highlight',
            name: 'Highlight',
            icon: 'mdi-marker',
            disabled: false,
          },
          {
            id: 'edit',
            name: 'Select',
            icon: 'mdi-cursor-default-outline',
            disabled: false,
          },
          {
            id: 'format',
            name: 'Format',
            icon: 'mdi-format-color-fill',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'mask',
            name: 'Mask',
            icon: 'mdi-transition-masked',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'stamp',
            name: 'Stamp',
            icon: 'mdi-stamper',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'addTestBlind',
            name: 'Test Blind',
            icon: 'mdi-brush-variant',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'symbols',
            name: 'Symbols',
            icon: 'mdi-symbol',
            hidden: isPermitModule(),
            disabled: false,
          },
        ],
        actionToolbarItems: [
          {
            id: 'link',
            name: 'Add Link',
            icon: 'mdi-link-plus',
            disabled: false,
          },
          {
            id: 'importLinks',
            name: 'Import Links',
            icon: 'mdi-database-import',
            disabled: false,
          },
          {
            id: 'rotate-right',
            name: 'Rotate',
            icon: 'mdi-rotate-right',
            hidden: isParentOrigin(),
            disabled: false,
          },
          {
            id: 'delete',
            name: 'Delete Selection',
            icon: 'mdi-delete-outline',
            color: 'error',
            disabled: false,
          },
        ],
        leftTools: [
          {
            id: 'text',
            name: 'Text',
            icon: 'mdi-format-text',
            disabled: false,
          },
          {
            id: 'shapes',
            name: 'Shapes',
            icon: 'mdi-shape',
            disabled: false,
          },
          {
            id: 'highlight',
            name: 'Highlight',
            icon: 'mdi-marker',
            disabled: false,
          },
          {
            id: 'format',
            name: 'Format',
            icon: 'mdi-format-color-fill',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'mask',
            name: 'Mask',
            icon: 'mdi-transition-masked',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'stamp',
            name: 'Stamp',
            icon: 'mdi-stamper',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'addTestBlind',
            name: 'Test Blind',
            icon: 'mdi-brush-variant',
            hidden: isPermitModule(),
            disabled: false,
          },
          {
            id: 'symbols',
            name: 'Symbols',
            icon: 'mdi-symbol',
            hidden: isPermitModule(),
            disabled: false,
          },
        ],
        bottomTools: [
          {
            id: 'link',
            name: 'Link',
            icon: 'mdi-link',
            disabled: false,
          },
          {
            id: 'comments',
            name: 'Comments',
            icon: 'mdi-comment',
            disabled: false,
          },
          {
            id: 'thumbnails',
            name: 'Pages View',
            icon: 'mdi-file-multiple',
            disabled: false,
          },
          {
            id: 'edit',
            name: 'Edit',
            icon: 'mdi-pencil',
            disabled: false,
          },
          {
            id: 'screenshot',
            name: 'Download',
            icon: 'mdi-download',
            disabled: false,
          },
          {
            id: 'undo',
            name: 'Undo',
            icon: 'mdi-undo',
            disabled: true,
          },
          {
            id: 'redo',
            name: 'Redo',
            icon: 'mdi-redo',
            disabled: true,
          },
          {
            id: 'rotate-right',
            name: 'Rotate',
            icon: 'mdi-rotate-right',
            hidden: isParentOrigin(),
            disabled: false,
          },
          {
            id: 'delete',
            name: 'Delete',
            icon: 'mdi-delete',
            color: 'error',
            disabled: false,
          },
          // {
          //     id: "filter",
          //     name: "Layers",
          //     icon: require('@/images/markupTools/filter.svg'),
          //     disabled: true
          // },
          {
            id: 'relatedDocuments',
            name: 'Related Documents',
            hidden: !isParentOrigin(),
            icon: 'mdi-file-document',
            disabled: false,
          },
          {
            id: 'discard',
            name: 'Discard',
            icon: 'mdi-cancel',
            color: 'error',
            disabled: false,
          },
          {
            id: 'save',
            name: 'Save',
            icon: 'mdi-content-save',
            disabled: false,
          },
        ],
        shapeTools: [
          {
            id: 'cloud',
            name: 'Cloud',
            icon: 'mdi-cloud-outline',
            color: 'white',
            disabled: false,
          },
          {
            id: 'circle',
            name: 'Ellipse',
            icon: 'mdi-ellipse-outline',
            color: 'white',
            disabled: false,
          },
          {
            id: 'polyline',
            name: 'polyline',
            icon: 'mdi-triangle-outline',
            color: 'white',
            disabled: false,
          },
          {
            id: 'rectangle',
            name: 'Rectangle',
            icon: 'mdi-rectangle-outline',
            color: 'white',
            disabled: false,
          },
          {
            id: 'line',
            name: 'Line',
            icon: 'mdi-vector-line',
            color: 'white',
            disabled: false,
          },
          {
            id: 'arrow',
            name: 'Arrow',
            icon: 'mdi-arrow-right',
            color: 'white',
            disabled: false,
          },
          {
            id: 'callout',
            name: 'Callout',
            icon: 'mdi-comment-arrow-right-outline',
            color: 'white',
            disabled: false,
          },
          {
            id: 'polycloud',
            name: 'Polycloud',
            icon: 'mdi-cloud-outline',
            color: 'white',
            disabled: false,
          },
          {
            id: 'freehand',
            name: 'Freehand',
            icon: 'mdi-pencil-outline',
            color: 'white',
            disabled: false,
          },
        ],
        currentMarkupsStyle: {
          'stroke-color': '#000000',
          'stroke-opacity': 1.0,
          'stroke-width': 0.15,
          'fill-color': '#000000',
          'fill-opacity': 0,
          'font-size': 20 /*Font Size*/ * 0.0225 /*Font Scale*/,
          'font-style': 'normal',
          'font-weight': 'normal',
          'font-family': 'Popins',
        },
        areToolsShown: false,
      };
    },
    async mounted() {
      this.isCommentsPanelActive = false;
      this.updateAreToolsShown();
      var _this = this;

      _addEvent(window, 'mousemove', (event) => {
        if (!_this.movingPopupInfo.isMoving) return;

        var markupPopupElem = $('.markups-popup')[0];
        var newLeft = _this.movingPopupInfo.left + (event.clientX - _this.movingPopupInfo.x);
        var newTop = _this.movingPopupInfo.top + (event.clientY - _this.movingPopupInfo.y);
        if (newLeft < 0) newLeft = 0;
        else if (markupPopupElem.offsetWidth + newLeft > markupPopupElem.parentElement.offsetWidth)
          newLeft = markupPopupElem.parentElement.offsetWidth - markupPopupElem.offsetWidth;
        markupPopupElem.style.left = newLeft + 'px';

        if (newTop < 0) newTop = 0;
        else if (markupPopupElem.offsetHeight + newTop > markupPopupElem.parentElement.offsetHeight)
          newTop = markupPopupElem.parentElement.offsetHeight - markupPopupElem.offsetHeight;
        markupPopupElem.style.top = newTop + 'px';
      });
      _addEvent(window, 'mouseup', () => {
        _this.movingPopupInfo.isMoving = false;
        if (_this.markupViewer && _this.markupViewer.impl) _this.markupViewer.resize();
      });

      var parentModuleUrl = _this.$store.getters['getParentModuleUrl'];
      var testPackageId = _this.$store.getters['getTestPackageId'];
      if (parentModuleUrl && testPackageId && !parentModuleUrl.toLowerCase().includes('permit')) {
        _this.pipingDocuments = await getAllPipingDocuments(parentModuleUrl, testPackageId);
      }
    },
    methods: {
      $has,
      updateAreToolsShown() {
        this.areToolsShown = this.$store.state.parentSiteName == 'reality' ? false : true;
      },
      togglePanel(panelName) {
        const index = this.expandedPanels.indexOf(panelName);
        if (index > -1) {
          this.expandedPanels.splice(index, 1);
        } else {
          this.expandedPanels.push(panelName);
        }
        // Resize viewer after panel toggle animation
        setTimeout(() => {
          if (this.markupViewer) {
            this.markupViewer.resize();
          }
        }, 300);
      },
      isPanelExpanded(panelName) {
        return this.expandedPanels.includes(panelName);
      },
      isToolActive(toolId) {
        const activeTool = this.$store.getters['getMarkupActiveTool'];
        if (['link', 'linkDrawings', 'linkCreation'].includes(activeTool)) {
          return toolId === 'link';
        }
        if (toolId === 'edit') {
          return this.autodeskMarkupsExtension?.duringEditMode;
        }
        // Check if any shape tool is active
        const shapeIds = this.shapeTools.map(s => s.id);
        if (toolId === 'shapes' && shapeIds.includes(activeTool)) {
          return true;
        }
        return activeTool === toolId;
      },
      getToolColor(item) {
        if (this.isToolActive(item.id)) {
          return 'white';
        }
        return item.color || 'primary';
      },
      isPanelExpandedFull(panelName) {
        // A panel takes full height if it's expanded and the other panel is collapsed
        const panels = ['links', 'comments'];
        const otherPanels = panels.filter(p => p !== panelName);
        const isExpanded = this.expandedPanels.includes(panelName);
        const othersCollapsed = otherPanels.every(p => !this.expandedPanels.includes(p));
        return isExpanded && othersCollapsed;
      },
      toggleThumbnailGrid() {
        this.showThumbnailGrid = !this.showThumbnailGrid;
        if (this.showThumbnailGrid) {
          this.$nextTick(() => {
            if (this.$refs.thumbnaillist) {
              this.$refs.thumbnaillist.getThumbnails();
            }
          });
        }
        // Resize viewer after toggle
        setTimeout(() => {
          if (this.markupViewer) {
            this.markupViewer.resize();
          }
        }, 300);
      },
      onPageChange(pageNumber) {
        this.openPageByNumber(pageNumber);
      },
      onHeaderMouseDown($event) {
        this.movingPopupInfo = {
          isMoving: true,
          left: $event.target.parentElement.offsetLeft,
          top: $event.target.parentElement.offsetTop,
          x: $event.clientX,
          y: $event.clientY,
        };
      },
      onShowToolbar($event) {
        $('.markup-tools:not(.shape-tools)').css('display', 'flex').hide().fadeIn(200);
        $('.toolbar-btn').hide(200);
      },
      onHideToolbar() {
        var _this = this;
        $('.markup-tools').hide(200);
        $('.toolbar-btn').show(200);
        setTimeout(function () {
          if (_this.markupViewer?.model?.getSeedUrn() == _this.attachment.url) {
            _this.markupViewer.resize();
          }
        }, 200);
      },
      saveNewMarkup() {

        let page = this.attachment.pagesMarkups.find(
          (o) => o.pageId / 1 == this.currentPageNumber / 1,
        );

        if (!page) {
          page = new ForgePageMarkup();
          page.pageId = this.currentPageNumber / 1;
          this.attachment.pagesMarkups.push(page);
        }

        saveAttachmentToDatabase(this.attachment);

        this.activeMarkupPage = page;
      },
      onComments() {
        this.isCommentsPanelActive = !this.isCommentsPanelActive;
      },
      onMarkupToolClicked($event) {

        var _this = this;
        var toolId = $event.id;
        var previousActiveTool = this.$store.getters['getMarkupActiveTool'];
        _this.previewingLink = null;
        //if any tool was active and clicked again, deactivate it
        if (previousActiveTool === toolId) {
          _this.setActiveTool('');
          _this.autodeskMarkupsExtension.leaveEditMode();
          if (toolId === 'thumbnails' || toolId === 'relatedDocuments') {
            setTimeout(() => {
              _this.markupViewer.resize();
            }, 500);
          }
          return;
        } else {
          if (toolId !== 'comments') {
            _this.setActiveTool(toolId);
          }
        }
        //Anyways reveal any css restricting stamps popup from showing
        //it should be only restricted by v-if
        $('.stamps-popup').css('display', 'flex');
        $('.testBlind-popup').css('display', 'flex');
        $('.symbols-popup').css('display', 'flex');

        switch (toolId) {
          case 'shapes':
            // Handled from the state and component(s)
            break;
          case 'format':
            // Handled from the state and component(s)
            break;
          case 'save':
            _this.onSave();
            break;
          case 'discard':
            _this.onDiscard();
            break;
          case 'delete':
            _this.onMarkupDelete();
            break;
          case 'undo':
            _this.onUndo();
            break;
          case 'redo':
            _this.onRedo();
            break;
          case 'rotate-right':
            _this.onRotatePdf(true);
            break;
          case 'link':
            // Handled from the state and component(s)
            break;
          case 'importLinks':
            // Handled from the state and ImportLinksPopup component
            break;
          case 'comments':
            _this.onComments();
            break;
          case 'filter':
            // code block
            break;
          case 'addTestBlind':
            // Handled from the state and component(s)
            break;
          case 'screenshot':
            _this.setActiveTool('screenshot');
            setTimeout(() => {
              _this.exportPDF();
            }, 50);
            break;
          case 'thumbnails':
            // Toggle pages panel
            if (!_this.expandedPanels.includes('pages')) {
              _this.expandedPanels.push('pages');
            }
            setTimeout(() => {
              _this.$refs.thumbnaillist?.getThumbnails();
            }, 500);
            break;
          case 'relatedDocuments':
            break;
          case 'edit':
            _this.ensureEditLayerExist();
            _this.autodeskMarkupsExtension.enterEditMode(_this.markupLayer);
            // By default, the enterEditMode is allow the user to create lines, we want to change it to be able to select only by creating an empty tool.
            _this.autodeskMarkupsExtension.changeEditMode(
              new Autodesk.Viewing.Extensions.Markups.Core.EditMode(
                _this.autodeskMarkupsExtension,
                null,
                [],
              ),
            );
            break;
          case 'stamp':
            //stamp
            break;
          case 'text':
          case 'highlight':
          case 'cloud':
          case 'circle':
          case 'polyline':
          case 'rectangle':
          case 'line':
          case 'arrow':
          case 'callout':
          case 'polycloud':
          case 'freehand':
          case 'mask':
            _this.startMarkupTool(toolId, _this.markupLayer);
            break;
          default:
        }
      },
      ensureEditLayerExist() {
        if (!this.autodeskMarkupsExtension) return;
        this.autodeskMarkupsExtension.enterEditMode(); //Enforce Creation of ext.editModeSvgLayerNode to nest new layer in
        this.autodeskMarkupsExtension.leaveEditMode(); //Leave edit mode to allow creation of new layer
        this.autodeskMarkupsExtension.loadMarkups('', this.markupLayer); //Enforce create new markups layer
      },
      setActiveTool(id) {
        this.$store.commit('setMarkupActiveTool', id);
      },
      onEditModeLeave() {
        var _this = this;
        if (_this.isFlutterRunning) {
          onEditModeLeaveFlutter();
        }
      },
      getStateStyle() {
        var format = this.$store.getters['getActiveMarkupsStyle'];
        this.format = format;
      },
      setStateStyle(style) {
        this.$store.commit('setActiveMarkupsStyle', style);
      },
      onStyleChanged(style) {
        var _this = this;
        _this.currentMarkupsStyle = style;
        var extension = _this.autodeskMarkupsExtension;
        if (extension.editMode) {
          extension.setStyle(style);
        } else {
          extension.enterEditMode(_this.markupLayer);
          extension.setStyle(style);
          extension.leaveEditMode();
        }
        _this.setStateStyle(style);
      },
      onRotatePdf(isClockwise) {
        var _this = this;
        var angle = isClockwise ? Math.PI / 2 : -Math.PI / 2;

        //Rotate Document
        rotateViewerCamera(_this.markupViewer, angle);

        //Rotate Markups
        var ext = _this.autodeskMarkupsExtension;
        if (ext) {
          rotateViewerMarkups(ext, angle);
        }

        //Rotate Active Page
        var page = _this.activeMarkupPage;
        if (page) {
          var newAngle = page.rotation + angle;
          page.rotation = newAngle % (2 * Math.PI);
        }

        _this.setActiveTool('');
      },
      onCreateStamp(stamp) {
        var _this = this;
        var layerName = _this.markupLayer;
        // var stampId = stamp.id;
        //get stamp image
        // var stampImageSelector = `.stamp-container#${stampId} svg`;
        // var stampImage = $(stampImageSelector)[0];
        if (/*stampImage != null &&*/ _this.stampsExtension != null) {
          //Just to create a new markup layer with LayerName
          _this.autodeskMarkupsExtension.enterEditMode(); //Enforce Creation of ext.editModeSvgLayerNode to nest new layer in
          _this.autodeskMarkupsExtension.leaveEditMode(); //Leave edit mode to allow creation of new layer
          _this.autodeskMarkupsExtension.loadMarkups('', layerName);
          _this.autodeskMarkupsExtension.enterEditMode(layerName);
          // _this.autodeskMarkupsExtension.leaveEditMode(); //Leave edit mode
          _this.stampsExtension.startDrawingFromSvgText(stamp.svgText, layerName);
        }
        //reset active tool or hide popup
        $('.stamps-popup').css('display', 'none');
      },
      onCreateSymbol(symbol) {
        var _this = this;
        var layerName = 'Layer_1';

        if (!_this.markupViewer) return;

        //Just to create a new markup layer with LayerName
        _this.autodeskMarkupsExtension.enterEditMode(); //Enforce Creation of ext.editModeSvgLayerNode to nest new layer in
        _this.autodeskMarkupsExtension.leaveEditMode(); //Leave edit mode to be able to do loadMarkups
        _this.autodeskMarkupsExtension.loadMarkups('', layerName); //Enforce create new markups layer
        _this.autodeskMarkupsExtension.enterEditMode(layerName);

        //get stamp image
        if (_this.stampsExtension != null) {
          var testBlindData = _this.$store.state.MARKUPS.testBlindData;
          var symbolId = testBlindData.id;
          var abbreviation = testBlindData.abbreviation;
          var isSequential = testBlindData.isSequential;
          if (testBlindData.isCombo) {
            abbreviation = abbreviation.split(',');
            isSequential = false;
          }
          var upperText = abbreviation ? abbreviation : 'XX';
          switch (symbolId) {
            case 'flag-pid-predefinded':
              _this.stampsExtension.startDrawingFlagSymbol(layerName, true);
              break;

            case 'flag-iso-predefinded':
              _this.stampsExtension.startDrawingFlagSymbol(layerName, false);
              break;

            default:
              _this.stampsExtension.startDrawingTestBlind(
                upperText,
                'XXX',
                layerName,
                isSequential,
                testBlindData.isCombo,
              );
              break;
          }
        }
        //reset active tool or hide popup
        $('.symbols-popup').css('display', 'none');
      },
      onCreateTestBlind(testBlindData) {
        var _this = this;
        var layerName = 'Layer_1';

        if (!_this.markupViewer) return;

        //Just to create a new markup layer with LayerName
        _this.autodeskMarkupsExtension.enterEditMode(); //Enforce Creation of ext.editModeSvgLayerNode to nest new layer in
        _this.autodeskMarkupsExtension.leaveEditMode(); //Leave edit mode to allow creation of new layer
        _this.autodeskMarkupsExtension.loadMarkups('', layerName); //Enforce create new markups layer
        _this.autodeskMarkupsExtension.enterEditMode(layerName);

        //get stamp image
        if (_this.stampsExtension != null) {
          var abbreviation = _this.$store.state.MARKUPS.testBlindData.abbreviation;
          var isSequential = _this.$store.state.MARKUPS.testBlindData.isSequential;
          var upperText = abbreviation ? abbreviation : 'XX';
          _this.stampsExtension.startDrawingTestBlind(upperText, 'XXX', layerName, isSequential);
        }
        //reset active tool or hide popup
        $('.testBlind-popup').css('display', 'none');
      },
      onLinkClassSelected($event) {
        var _this = this;
        if ($event.length > 0) {
          _this.selectedLinkClass = [...$event];
        }
      },
      onCreateMarkupLink($event) {
        var _this = this;
        _this.selectedLinkDrawing = $event;

        if (_this.autodeskMarkupsExtension && _this.autodeskMarkupsExtension.editMode) {
          _this.autodeskMarkupsExtension.leaveEditMode();
        }

        // Create preview element for the link icon
        _this.linkPreviewElement = _this.createLinkPreviewElement(_this.selectedLinkDrawing.type);
        document.body.appendChild(_this.linkPreviewElement);

        _this.markupViewer.canvas.style.cursor = 'none';
        _this.markupViewer.canvas.addEventListener('mousemove', _this.onCreateMarkupLinkMouseMove);
        _this.markupViewer.canvas.addEventListener('click', _this.onCreateMarkupLinkClick);
      },
      createLinkPreviewElement(linkType) {
        const linkColor = LINK_TYPE_COLORS[linkType] || LINK_TYPE_COLORS.default;

        const container = document.createElement('div');
        container.className = 'link-preview-cursor';
        container.style.cssText = `
          position: fixed;
          pointer-events: none;
          z-index: 99999;
          opacity: 0.5;
          transform: translate(-50%, -50%);
        `;

        if (linkType === 'P&ID') {
          // Chevrons style << • >>
          container.style.cssText += `
            display: flex;
            align-items: center;
            gap: 2px;
          `;

          // Left chevrons
          for (let i = 0; i < 2; i++) {
            const chevron = document.createElement('div');
            chevron.style.cssText = `
              width: 6px;
              height: 6px;
              border-left: 2px solid ${linkColor};
              border-bottom: 2px solid ${linkColor};
              transform: rotate(45deg);
            `;
            container.appendChild(chevron);
          }

          // Dot
          const dot = document.createElement('div');
          dot.style.cssText = `
            width: 8px;
            height: 8px;
            background: ${linkColor};
            border-radius: 50%;
            margin: 0 2px;
          `;
          container.appendChild(dot);

          // Right chevrons
          for (let i = 0; i < 2; i++) {
            const chevron = document.createElement('div');
            chevron.style.cssText = `
              width: 6px;
              height: 6px;
              border-right: 2px solid ${linkColor};
              border-top: 2px solid ${linkColor};
              transform: rotate(45deg);
            `;
            container.appendChild(chevron);
          }
        } else {
          // Circles style
          container.style.cssText += `
            width: 24px;
            height: 24px;
          `;

          // Outer circle
          const outerCircle = document.createElement('div');
          outerCircle.style.cssText = `
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 24px;
            height: 24px;
            border: 2px solid ${linkColor};
            border-radius: 50%;
            box-sizing: border-box;
          `;
          container.appendChild(outerCircle);

          // Inner circle
          const innerCircle = document.createElement('div');
          innerCircle.style.cssText = `
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 16px;
            height: 16px;
            border: 2px solid ${linkColor};
            border-radius: 50%;
            box-sizing: border-box;
          `;
          container.appendChild(innerCircle);

          // Dot
          const dot = document.createElement('div');
          dot.style.cssText = `
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            width: 8px;
            height: 8px;
            background: ${linkColor};
            border-radius: 50%;
          `;
          container.appendChild(dot);
        }

        return container;
      },
      onCreateMarkupLinkMouseMove(event) {
        var _this = this;
        if (_this.linkPreviewElement) {
          _this.linkPreviewElement.style.left = event.clientX + 'px';
          _this.linkPreviewElement.style.top = event.clientY + 'px';
        }
      },
      async onCreateMarkupLinkClick(event) {
        event.preventDefault();
        event.stopPropagation();
        var _this = this;

        // Remove listeners and preview element
        _this.markupViewer.canvas.removeEventListener('mousemove', _this.onCreateMarkupLinkMouseMove);
        _this.markupViewer.canvas.removeEventListener('click', _this.onCreateMarkupLinkClick);
        if (_this.linkPreviewElement) {
          _this.linkPreviewElement.remove();
          _this.linkPreviewElement = null;
        }
        _this.markupViewer.canvas.style.cursor = 'pointer';

        // Get world position from click
        var worldPositionObj = _this.markupViewer.clientToWorld(
          event.offsetX,
          event.offsetY,
          true,
          true,
        );

        if (!worldPositionObj) {
          return;
        }

        var worldPosition = worldPositionObj.point;
        var attachment = _this.attachment;

        if (!attachment.pagesMarkups) {
          attachment.pagesMarkups = [];
        }
        let page = attachment.pagesMarkups.find((o) => o.pageId / 1 == _this.currentPageNumber / 1);
        if (!page) {
          page = new ForgePageMarkup();
          page.pageId = _this.currentPageNumber / 1;
          attachment.pagesMarkups.push(page);
        }

        if (!page.links) {
          page.links = [];
        }

        var link = new ForgeMarkupLink();
        link.linkedAttachmentId = _this.selectedLinkDrawing.id;
        if (isParentOrigin()) {
          link.referenceDocumentId = _this.selectedLinkDrawing.id;
          link.referenceContextId = _this.selectedLinkDrawing.TestPackageID;
          link.referenceContentId = _this.selectedLinkDrawing.DocumentOutputID;

          var dbAttachment = await getAttachmentByReference(
            link.referenceDocumentId,
            link.referenceContextId,
            link.referenceContentId,
          );
          if (dbAttachment && dbAttachment.id) {
            link.linkedAttachmentId = dbAttachment.id;
          } else {
            dbAttachment = new ForgeAttachment();
            dbAttachment.guid = link.referenceDocumentId;
            dbAttachment.referenceDocumentId = link.referenceDocumentId;
            dbAttachment.referenceContextId = link.referenceContextId;
            var savedAttachment = await postAttachment(dbAttachment);
            link.linkedAttachmentId = savedAttachment?.id;
          }
        }
        link.name = _this.selectedLinkDrawing.displayName;
        link.type = _this.selectedLinkDrawing.type;
        if (link.type == 'P&ID') {
          link.documentId = _this.selectedLinkDrawing.documentId;
        }
        else if (link.type == 'CML' || link.type == 'TML') {
          // CML/TML links carry BOTH cmlId (navigation target) and tagId (the
          // master tag) so they participate in the existing TagId-keyed cleanup
          // (InspectionDeletionService scrubs ForgeMarkupLinks WHERE TagId = …).
          // For TML, tmlPointId rides in link.metadata (no schema column needed).
          link.cmlId = _this.selectedLinkDrawing.cmlId;
          link.tagId = _this.selectedLinkDrawing.tagId;
        }
        else {
          link.tagId = _this.selectedLinkDrawing.tagId;
        }
        link.metadata = JSON.stringify(_this.selectedLinkDrawing);
        link.position = new THREE.Vector3(worldPosition.x, worldPosition.y, 0);
        link.width = 0;
        link.height = 0;
        link.rotation = 0;
        if (_this.selectedLinkDrawing.imgUrl) {
          link.thumbnailUrl = _this.selectedLinkDrawing.imgUrl;
        } else if (_this.selectedLinkDrawing.thumbnailUrl) {
          link.thumbnailUrl = _this.selectedLinkDrawing.thumbnailUrl;
        }
        if (page.id) {
          link.forgePageMarkupId = page.id;
        }
        page.links.push(link);

        _this.renderLinkOnViewer(link);

        _this.setActiveTool('');
      },
      onImportLinks(importData) {

        var _this = this;
        const { links, rotation, stats, extractedRevision } = importData;

        if (!links || links.length === 0) {
          this.$dialog.notify.warning('No tags to import.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }

        // Clear any previously imported links that weren't saved
        _this.removeImportedLinks();

        var attachment = _this.attachment;
        if (!attachment.pagesMarkups) {
          attachment.pagesMarkups = [];
        }

        let page = attachment.pagesMarkups.find(
          (o) => o.pageId / 1 == _this.currentPageNumber / 1,
        );
        if (!page) {
          page = new ForgePageMarkup();
          page.pageId = _this.currentPageNumber / 1;
          attachment.pagesMarkups.push(page);
        }

        if (!page.links) {
          page.links = [];
        }

        // Reset imported link tracking
        _this.importedLinkIds = [];

        // Get viewer bounds to calculate positions
        const modelBBox = _this.markupViewer?.model?.getBoundingBox();
        const viewerWidth = modelBBox ? (modelBBox.max.x - modelBBox.min.x) : 100;
        const viewerHeight = modelBBox ? (modelBBox.max.y - modelBBox.min.y) : 100;

        // Import each link - just draw tags at their positions
        links.forEach((linkData, index) => {
          const THREE = window.THREE;
          var link = new ForgeMarkupLink();

          // Generate a unique ID

          // Set link properties - no matching, just store the tag info
          link.name = linkData.name || linkData.displayName;
          link.type = linkData.type;
          link.isImporting = true; // Mark as importing - will be removed if cancelled
          link.documentId = linkData.documentId;
          link.tagId = linkData.tagId;
          //link.metadata = JSON.stringify(linkData.originalData || linkData);

          // Track imported link ID for potential rollback
          _this.importedLinkIds.push(link.id);

          // Calculate position from normalized coords or distribute in grid
          let posX, posY;
          if (linkData.position && linkData.position.x !== undefined) {
            // Convert normalized (0-1) coords to viewer coords
            const minX = modelBBox ? modelBBox.min.x : 0;
            const minY = modelBBox ? modelBBox.min.y : 0;
            posX = minX + linkData.position.x * viewerWidth;
            posY = minY +  linkData.position.y * viewerHeight; // Flip Y axis
          }

          link.position = new THREE.Vector3(posX, posY, 0);

          // Store normalized bbox dimensions for box rendering after save/reload
          const coords = linkData.position;
          link.width = coords?.width || 0;
          link.height = coords?.height || 0;
          link.rotation = rotation;

          // Store full V7 source data for box reconstruction
          link.metadata = JSON.stringify({
            v7BoundingBox: linkData.boundingBox,
            normalizedCenter: { x: coords?.x, y: coords?.y },
            normalizedWidth: coords?.width || 0,
            normalizedHeight: coords?.height || 0,
            importRotation: rotation,
            source: 'v7-import',
          });

          // Add to page links
          if (page.id) {
            link.forgePageMarkupId = page.id;
          }
          page.links.push(link);

          // Render link on viewer using current display mode
          _this.renderLinkOnViewer(link);
        });

        _this.activeMarkupPage = page;

        // Show success message with stats
        this.$dialog.notify.success(
          `Imported ${stats.imported} tags successfully!`,
          {
            position: 'top-right',
            timeout: 4000,
          },
        );

        // Prompt for revision update if revision was found in V7 JSON
        if (extractedRevision && _this.attachment?.referenceDocumentId) {
          _this.$nextTick(() => {
            _this.$dialog.confirm({
              text: `The imported JSON contains document revision <strong>${extractedRevision}</strong>.<br><br>Would you like to update this document's revision to <strong>${extractedRevision}</strong>?`,
              title: 'Update Document Revision',
              actions: {
                false: 'No',
                true: {
                  text: 'Yes, Update',
                  color: 'primary',
                  handle: () => {
                    _this.updateDocumentRevision(_this.attachment.referenceDocumentId, extractedRevision);
                  },
                },
              },
            });
          });
        }

        _this.setActiveTool('');
      },
      async updateDocumentRevision(documentId, revisionNumber) {
        try {
          const documentsService = (await import('@/features/Task/service/documents.service')).default;
          const response = await documentsService.getById(documentId);
          const document = response.data || response;

          if (document) {
            document.revisionNumber = revisionNumber;
            await documentsService.update(document);

            this.$dialog.notify.success(
              `Document revision updated to ${revisionNumber}`,
              { position: 'top-right', timeout: 3000 },
            );
          }
        } catch (err) {
          console.error('Failed to update document revision:', err);
          this.$dialog.notify.error(
            'Failed to update document revision',
            { position: 'top-right', timeout: 4000 },
          );
        }
      },
      onImportDiscard() {
        // Called when user discards the import process
        // Clean up any imported links that weren't saved
        this.removeImportedLinks();
        this.setActiveTool('');
      },
      onDeleteMarkupLink(link) {
        var _this = this;
        if (!link) return;
        _this.previewingLink = null;

        // Hide the link element from the viewer (don't remove yet - wait for save)
        const linkId = link.id || link.linkedAttachmentId;
        // Hide icon elements
        const linkElements = document.querySelectorAll('.markup-link-popup');
        linkElements.forEach((el) => {
          const elId = el.getAttribute('data-id') || el.id;
          if (elId === linkId) {
            el.style.display = 'none';
            el.classList.add('pending-removal');
          }
        });
        // Hide box elements
        _this.importBoxElements.forEach((el) => {
          if (el.getAttribute('data-id') === linkId) {
            el.style.display = 'none';
          }
        });
      },
      onRestoreMarkupLink(link) {
        var _this = this;
        if (!link) return;

        // Show the link element again
        const linkId = link.id || link.linkedAttachmentId;
        // Restore icon elements
        const linkElements = document.querySelectorAll('.markup-link-popup');
        linkElements.forEach((el) => {
          const elId = el.getAttribute('data-id') || el.id;
          if (elId === linkId) {
            el.style.display = 'block';
            el.classList.remove('pending-removal');
          }
        });
        // Restore box elements
        _this.importBoxElements.forEach((el) => {
          if (el.getAttribute('data-id') === linkId) {
            el.style.display = 'block';
          }
        });
      },

      /**
       * Render a link on the viewer using the current display mode.
       * V7-imported links with metadata can be shown as boxes; all others use icons.
       */
      renderLinkOnViewer(link) {
        if (this.linkDisplayMode === 'boxes' && link.metadata) {
          this.drawLinkAsBox(link);
        } else {
          this.drawLinkAsIcon(link);
        }
      },

      /**
       * Render a link as a standard icon marker (existing behavior).
       */
      drawLinkAsIcon(link) {
        const iconOptions = {
          position: link.position,
          width: 0,
          height: 0,
          rotation: 0,
          differences: null,
          title: link.name,
          id: link.id ?? link.linkedAttachmentId,
          className: 'markup-link-popup',
          forgeViewer: this.markupViewer,
          linkType: link.type,
          metadata: link.metadata, // Include metadata for data-metadata attribute
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.onMarkupLinkClick(e, link);
          },
          addContent: true,
          removeTildeSign: true,
        };
        const labelElement = this.forgeService.create3DIconToViewer(iconOptions);
        if (labelElement) {
          if (link.type) {
            labelElement.setAttribute('data-tag-type', link.type);
          }
          if (link.cmlId) {
            labelElement.setAttribute('data-cml-id', link.cmlId);
          }
        }
      },

      /**
       * Render a V7-imported link as a bounding box rectangle.
       * Reconstructs box dimensions from stored normalized width/height + metadata.
       * Falls back to icon rendering if metadata is missing or invalid.
       */
      drawLinkAsBox(link) {
        let meta;
        try { meta = JSON.parse(link.metadata); } catch { return this.drawLinkAsIcon(link); }
        if (!meta?.source || meta.source !== 'v7-import') return this.drawLinkAsIcon(link);

        const modelBBox = this.markupViewer?.model?.getBoundingBox();
        if (!modelBBox) return this.drawLinkAsIcon(link);

        const viewerW = modelBBox.max.x - modelBBox.min.x;
        const viewerH = modelBBox.max.y - modelBBox.min.y;

        // link.position is already world-space center (Vector3)
        const worldCenterX = link.position.x;
        const worldCenterY = link.position.y;

        // Convert normalized dims to world dims
        // Handle rotation swap: for 90°/270°, W and H were normalized pre-rotation
        const rot = meta.importRotation || 0;
        const isRotated = rot === 90 || rot === 270;
        const normW = isRotated ? link.height : link.width;
        const normH = isRotated ? link.width : link.height;
        const worldW = normW * viewerW;
        const worldH = normH * viewerH;

        const worldBounds = {
          minX: worldCenterX - worldW / 2,
          maxX: worldCenterX + worldW / 2,
          minY: worldCenterY - worldH / 2,
          maxY: worldCenterY + worldH / 2,
        };

        const box = createAnnotationBoxElement(this.markupViewer, worldBounds, {
          id: link.id ?? link.linkedAttachmentId,
          name: link.name,
          type: link.type,
          boxSource: 'saved',
          clickable: true,
          onClick: (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.onMarkupLinkClick(e, link);
          },
        });

        if (!box) return this.drawLinkAsIcon(link);

        // Add data attributes for CML link highlighting
        if (link.cmlId) {
          box.setAttribute('data-cml-id', link.cmlId);
        }
        if (link.metadata) {
          box.setAttribute('data-metadata', link.metadata);
        }

        if (this.markupViewer?.container) {
          this.markupViewer.container.appendChild(box);
        }
        this.importBoxElements.push(box);

        // Ensure camera handler is registered
        if (!this._importBoxCameraHandler) {
          this._importBoxCameraHandler = () => updateBoxPositions(this.markupViewer, this.importBoxElements);
          this.markupViewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, this._importBoxCameraHandler);
        }
      },

      /**
       * Remove all post-import annotation box DOM elements and clean up camera listener.
       */
      removeImportBoxElements() {
        const result = cleanupBoxElements(
          this.importBoxElements, this.markupViewer,
          this._importBoxCameraHandler, 'saved',
        );
        this.importBoxElements = result.elements;
        this._importBoxCameraHandler = result.cameraHandler;
      },

      /**
       * Re-render all visible links using the current display mode.
       * Called when linkDisplayMode changes.
       */
      refreshLinkDisplay() {
        // Remove all existing link elements (both icons and boxes)
        if (this.markupViewer?.container) {
          $(this.markupViewer.container).find('.markup-link-popup').remove();
        }
        this.removeImportBoxElements();

        // Re-render all links from active page
        const page = this.activeMarkupPage;
        if (page?.links?.length > 0) {
          page.links.forEach(link => this.renderLinkOnViewer(link));
        }
      },
      /**
       * Remove all imported links that haven't been saved yet
       * Called when discarding changes or before a new import
       */
      removeImportedLinks() {
        var _this = this;

        if (!_this.importedLinkIds || _this.importedLinkIds.length === 0) {
          return;
        }

        // Find current page markup
        var page = _this.attachment?.pagesMarkups?.find(
          (o) => o.pageId / 1 == _this.currentPageNumber / 1,
        );

        if (page && page.links) {
          // Remove imported links from the page data
          page.links = page.links.filter(link => !_this.importedLinkIds.includes(link.id));
        }

        // Remove the DOM elements from viewer (both icons and boxes)
        _this.importedLinkIds.forEach((linkId) => {
          // Remove icon elements
          const linkElements = document.querySelectorAll('.markup-link-popup');
          linkElements.forEach((el) => {
            const elId = el.getAttribute('data-id') || el.id;
            if (elId === linkId) {
              el.remove();
            }
          });

          // Remove box elements
          _this.importBoxElements = _this.importBoxElements.filter((el) => {
            if (el.getAttribute('data-id') === linkId) {
              el.remove();
              return false;
            }
            return true;
          });
        });

        // Clear the tracking array
        _this.importedLinkIds = [];
      },
      /**
       * Clear the isImporting flag on all links (called after successful save)
       */
      clearImportingFlags() {
        var _this = this;

        // Find current page markup
        var page = _this.attachment?.pagesMarkups?.find(
          (o) => o.pageId / 1 == _this.currentPageNumber / 1,
        );

        if (page && page.links) {
          page.links.forEach(link => {
            if (link.isImporting) {
              delete link.isImporting;
            }
          });
        }

        // Clear the tracking array since they're now saved
        _this.importedLinkIds = [];
      },
      removeSelectedLinksFromPageMarkup() {
        var _this = this;

        // Get selected links from the panel
        const linksPanel = _this.$refs.markupLinksPanel;
        if (!linksPanel) return;

        const linksToRemove = linksPanel.getSelectedLinksForRemoval();
        if (!linksToRemove || linksToRemove.length === 0) return;

        // Find current page markup
        var page = _this.attachment?.pagesMarkups?.find(
          (o) => o.pageId / 1 == _this.currentPageNumber / 1,
        );

        if (page && page.links) {
          page.deletedLinkIds = page.deletedLinkIds ?? [];

          linksToRemove.forEach((link) => {
            const linkIndex = link.id
              ? page.links.findIndex((pageLink) => pageLink.id === link.id)
              : page.links.indexOf(link);
            if (linkIndex > -1) {
              const [removedLink] = page.links.splice(linkIndex, 1);
              if (removedLink.id && !page.deletedLinkIds.includes(removedLink.id)) {
                page.deletedLinkIds.push(removedLink.id);
              }
            }
          });

          // Also remove the DOM elements
          linksToRemove.forEach((link) => {
            const linkId = link.id || link.linkedAttachmentId;
            const linkElements = document.querySelectorAll('.markup-link-popup');
            linkElements.forEach((el) => {
              const elId = el.getAttribute('data-id') || el.id;
              if (elId === linkId) {
                el.parentElement?.removeChild(el);
              }
            });
          });

          // Clear the selection in the panel (without restoring since we're saving)
          linksPanel.clearSelectionWithoutRestore();
        }
      },
      onLinksPanelLinkClicked(link) {
        // Handle link click from the links panel - show preview
        this.previewingLink = link;
        onMarkupLinkClickedFlutter(link);
      },
      onLinksPanelLinkSelected(link) {
        // Handle link selection from the links panel
        this.previewingLink = link;
        this.$store.commit('setPdfViewerPreviewingLink', link);
      },
      onLinkVisibilityChanged({ link, visible }) {
        // Handle link visibility toggle
        log(`Link ${link.name} visibility changed to ${visible}`);
      },
      onMarkupLinkClick($event, link) {
        var _this = this;
        _this.previewingLink = link;
        onMarkupLinkClickedFlutter(link);
        var element = $event.srcElement;
        if (!element.classList.contains('markup-link-popup')) {
          element = element.closest('.markup-link-popup');
        }
        _this.$store.commit('setPreviewingLinkElement', element);
        _this.cameraPosition = _this.markupViewer.getCamera().position;
      },
      onMarkupLinkOpen(link) {

        var _this = this;
        _this.link = link;
        log('onMarkupLinkOpen', link);
        _this.onSave(false, async () => {

          if (isParentOrigin()) {
            let markupsComp = this.$store.state.markupsComponent
              ? this.$store.state.markupsComponent
              : getMarkupsComponent();
            let link = _this.link;

            let linkMetadata = link.metadata ? JSON.parse(link.metadata) : '';
            let parentModuleUrl = _this.$store.getters['getParentModuleUrl'];
            let controller = 'documentrevisionfilepage';
            let fileUrl = getPipingDrawingLinkUrl(parentModuleUrl, controller, linkMetadata);

            if (!parentModuleUrl || !fileUrl) {
              return;
            }
            if (linkMetadata.id && linkMetadata.TestPackageID && linkMetadata.DocumentOutputID) {
              let attachment;
              if (link.linkedAttachmentId) {
                attachment = await getAttachment(link.linkedAttachmentId);
              } else {
                attachment = await getAttachmentByReference(
                  linkMetadata.id,
                  linkMetadata.TestPackageID,
                  linkMetadata.DocumentOutputID,
                );
              }
              if (!attachment || !attachment.id) {
                //Attachment not in DB yet
                attachment.referenceDocumentId = linkMetadata.id;
                attachment.referenceContextId = linkMetadata.TestPackageID;
                attachment.referenceContentId = linkMetadata.DocumentOutputID;
                attachment.referencePageNumber = linkMetadata.PageNumber;
                attachment.absoluteUrl = linkMetadata.AbsolutePath;
                attachment.name = linkMetadata.DocumentNumber;
              }
              attachment.url = fileUrl;

              _this.$store.commit('setActiveAttachment', attachment);
              _this.showAttachment(attachment, true, 1);

              postAttachment(attachment, (attachmentData) => {
                attachment.id = attachmentData?.id;
              });
            } else if (link.linkedAttachmentId) {
              let attachment = await getAttachment(link.linkedAttachmentId);
              if (!attachment) {
                error(`No Attachment found by ID: ${link.linkedAttachmentId}`);
              }
              if (!attachment?.id) {
                //Attachment not in DB yet
                attachment.id = link.linkedAttachmentId;
              }
              attachment.url = fileUrl;
              _this.$store.commit('setActiveAttachment', attachment);
              _this.autodeskMarkupsExtension?.unloadMarkups('Layer_1');
              _this.markupViewer.finish();
              _this.markupViewer = null;
              Autodesk.Viewing.shutdown();
              _this.showAttachment(attachment, true, 1);
            }
          } else {
            let markupsComp = this.$store.state.markupsComponent
              ? this.$store.state.markupsComponent
              : getMarkupsComponent();
            let link = markupsComp.link;
            let linkMetadata = JSON.parse(link.metadata);
            let classId = linkMetadata.classId;
            let linkedAttachmentId = link.linkedAttachmentId;
            switch (classId) {
              case 'attachments':
                var attachment = await getAttachment(linkedAttachmentId);
                if (!attachment.id) {
                  //Attachment not in DB yet
                  attachment.id = linkedAttachmentId;
                }
                _this.$store.commit('setActiveAttachment', attachment);
                _this.autodeskMarkupsExtension?.unloadMarkups('Layer_1');
                _this.showAttachment(attachment);
                postAttachment(attachment, () => {
                  //this.$store.dispatch('RequestPIdsLinkedTags');
                });
                break;
              default:
                break;
            }
          }
        });
      },
      show(annotation, markup, pageGuid = null) {
        this.updateAreToolsShown();
        this.$store.commit('setMarkupsPopupStatus', PopupStatus.ACTIVE);
        this.annotation = annotation;
        this.attachment = markup;
        this.shown = true;
        this.isCropping = false;
        this.currentCropBox = null;
        $('.markups-popup').css('display', 'flex');
        setTimeout(() => {
          this.showMarkupInViewer(pageGuid);
        }, 500);
        ForgeMessage.sendMessageToParent('markupsPopupOpened', null);
      },
      showAttachment(attachment, isFullscreen = false, pageNumber = 1) {
        this.isCommentsPanelActive = false;
        this.updateAreToolsShown();
        var _this = this;
        if (!pageNumber || isNaN(pageNumber / 1) || pageNumber < 1) {
          pageNumber = 1;
        }
        if (isFullscreen) {
          _this.enterFullscreen();
          _this.$store.commit('setMarkupsPopupStatus', PopupStatus.FULLSCREEN);
        } else {
          _this.$store.commit('setMarkupsPopupStatus', PopupStatus.ACTIVE);
        }
        _this.attachment = attachment;
        if (!_this.attachment.pagesMarkups) {
          _this.attachment.pagesMarkups = [];
        }
        var page;
        var pages = _this.attachment.pagesMarkups.filter((o) => o.pageId / 1 == pageNumber / 1);
        if (pages && pages.length) {
          for (var i = 0; i < pages.length; i++) {
            let p = pages[i];
            if (p && p.markupText != '[]') {
              if (page) {
                _this.attachment.pagesMarkups.pop(page);
              }
              page = p;
            }
          }
        } else {
          page = new ForgePageMarkup();
          page.pageId = pageNumber / 1;
          _this.attachment.pagesMarkups.push(page);
        }
        _this.activeMarkupPage = page;
        _this.$store.commit('setActiveAttachmentPage', pageNumber);
        _this.currentPageNumber = pageNumber / 1;
        _this.shown = true;
        _this.isCropping = false;
        _this.currentCropBox = null;
        $('.markups-popup').css('display', 'flex');
        $('.screen-overlay').css('display', 'block');

        if (!_this.attachment.url) {
          _this.$dialog.notify.warning('Cannot find document.', {
            position: 'top-right',
            timeout: 3000,
          });
          return;
        }
        setTimeout(() => {
          if (_this.markupViewer) {
            _this.openPageByNumber(pageNumber);
          } else {
            _this.launchPDFViewer(pageNumber);
          }
        }, 200);

        $('.markup-tools').css('display', 'flex').hide().fadeIn(200);
        $('.toolbar-btn').hide(200);

        ForgeMessage.sendMessageToParent('markupsPopupOpened', null);
        /*
                Handler from reality should be:
                var forgeIframe = $('#forgeiFrame')
                forgeIframe.css('position', 'relative')
                forgeIframe.css('z-index', '100')
            */
      },
      exportPDF() {
        this.markupViewer.resize();
        setTimeout(() => {
          let fileName = 'markups';
          exportMarkupImageWithModel(this.markupViewer, true, false, fileName, () => {
            this.setActiveTool('');
          });
        }, 50);
      },

      enterFullscreen() {
        this.$store.commit('setMarkupsPopupStatus', PopupStatus.FULLSCREEN);
      },
      hidePopup() {
        if (this.markupViewer) {
          this.markupViewer.finish();
          this.setActiveTool('');
          this.markupViewer = null;
        }

        this.previewingLink = null;

        if (this.$store.state.pdfviewer) {
          pdfViewerOpenPageByNumber(this.$store.state.MARKUPS.activeAttachmentPage);
        }

        this.$store.commit('setMarkupsPopupStatus', PopupStatus.HIDDEN);

        this.onHideToolbar();
        $('.markups-popup').css('display', 'none');
        $('.screen-overlay').css('display', 'none');

        ForgeMessage.sendMessageToParent('markupsPopupClosed', null);
        /*
                Handler from reality should be:
                var forgeIframe = $('#forgeiFrame')
                forgeIframe.css('position', '')
                forgeIframe.css('z-index', '')
            */

        //Publish Message that Forge Markups Iframe is hidden
        var markupsPopupClosedMessage = new ForgeMessage('markups-popup-closed', null);
        markupsPopupClosedMessage.send();
      },
      minimize() {
        this.$store.commit('setMarkupsPopupStatus', PopupStatus.MINIMIZED);
      },
      startCropping() {
        if (!this.markupViewer) return;
        this.isCropping = !this.isCropping;

        let croppingEx = this.markupViewer.getExtension('Cropping2DExtension');
        let page = this.attachment.pagesMarkups.find((o) => o.pageId / 1 == this.currentPageId / 1);
        let box = page ? page.cropBox : null;
        if (!box) box = this.currentCropBox;
        croppingEx.setCropping(box, this.isCropping);
        if (this.isCropping) this.autodeskMarkupsExtension.leaveEditMode();
        else this.autodeskMarkupsExtension.enterEditMode();
      },
      //Default method
      async launchPDFViewer(pageNumber = 1) {
        var _this = this;
        var markupViewerContainer = document.getElementById('markup-viewer');
        var pdfUrl = await fetchDocumentAsObjectUrl(_this.attachment);
        if (!pdfUrl) return;
        if (_this.markupViewer) {
          _this.autodeskMarkupsExtension?.unloadMarkups('Layer_1');
          _this.markupViewer?.unloadExtension('Autodesk.Viewing.MarkupsCore');
          _this.markupViewer?.finish();
        }
        var isCropping2DExtLoaded = isScriptLoaded('Cropping2DExtension');
        var extensions = ['Autodesk.DocumentBrowser'];
        if (isCropping2DExtLoaded) {
          extensions.push('Cropping2DExtension');
        }
        Autodesk.Viewing.Initializer({}, () => {
          // Auth headers required for non-document Forge API requests (3D models, attachments, etc.)
          Autodesk.Viewing.endpoint.HTTP_REQUEST_HEADERS = $getAuthHeaders();
          var config = {
            extensions: extensions,
            disabledExtensions: {
              measure: true,
            },
            skipPropertyDb: false,
          };
          _this.markupViewer = new Autodesk.Viewing.Private.GuiViewer3D(
            markupViewerContainer,
            config,
          );
          _this.markupViewer.setTheme('light-theme');
          _this.markupViewer.start();
          const img = require('../../images/RealityLoaderNew.png');
          $('.forge-spinner img').attr('src', img);

          // Clear old thumbnails from the old viewer.
          if (_this && _this.$refs && _this.$refs.thumbnaillist) {
            _this.$refs.thumbnaillist.clearThumbnails();
          }

          _this.markupViewer.addEventListener(Autodesk.Viewing.MODEL_ROOT_LOADED_EVENT, () => {
            // if the attachment is pdf then we need to open the thumbnails panel.
            if (_this.isMarkupImage) {
              _this.setActiveTool(null);
            } else {
              if (!isParentOrigin()) {
                // Expand pages panel instead of setting active tool
                if (!_this.expandedPanels.includes('pages')) {
                  _this.expandedPanels.push('pages');
                }
                setTimeout(() => {
                  if (_this && _this.$refs && _this.$refs.thumbnaillist) {
                    _this.$refs.thumbnaillist.getThumbnails();
                  }
                }, 500);
              }
            }
          });

          let isFirefox = /Firefox/i.test(navigator.userAgent);
          var mousewheel = isFirefox ? 'DOMMouseScroll' : 'mousewheel';
          _this.markupViewer.canvas.addEventListener(
            mousewheel,
            (event) => {
              var cameraPosition = _this.markupViewer.navigation.getPosition();
              let delta = isFirefox ? event.detail : event.wheelDelta;
              var isZooming = delta > 0;
              if (cameraPosition.z > _this.cameraMaxZ && !isZooming) {
                event.stopPropagation();
                event.preventDefault();
              }
            },
            false,
          );

          _this.markupViewer.addEventListener(Autodesk.Viewing.CAMERA_CHANGE_EVENT, (e) => {
            this.forgeService.updateLabelsPositionsByClassName(
              'markup-link-popup',
              _this.markupViewer,
            );
            this.forgeService.updateLabelsPositionsByClassName(
              'markups-link-preview-popup-container',
              _this.markupViewer,
            );
            this.forgeService.updateLabelsPositionsByClassName(
              'markup-delete-floating-button',
              _this.markupViewer,
            );
            this.forgeService.updateLabelsPositionsByClassName(
              'markup-comment',
              _this.markupViewer,
            );
            var cameraPosition = e.camera?.position;
            if (cameraPosition && !cameraPosition.equals(_this.cameraPosition)) {
              _this.previewingLink = null;
            }
          });

          _this.markupViewer
            .loadExtension('Autodesk.PDF', {
              verbosityLevel: 0,
              enableMSDFText: false,
              useTextLayer: false,
              enableHyperlinks: false,
              enableTextSearch: false,
            })
            .then(() => {
              var url = this.isMarkupImage ? getEmptyFileUrl() : pdfUrl;
              _this.markupViewer.loadModel(
                url,
                {
                  page: pageNumber,
                },
                (doc) => {
                  if (_this.isFlutterRunning) {
                    onMarkupViewerLoaded();
                  } else {
                    _this.openPageByNumber(pageNumber); //Just to skip font type issues
                  }
                  _this.getAttachmentPagesCount();
                  _this.cameraMaxZ = 2 * _this.markupViewer.navigation.getCamera().position.z;
                  _this.markupViewer.navigation.setReverseZoomDirection(true);
                  $('#markup-viewer #settingsTools').css('display', 'none');
                  _this.markupViewer.setBackgroundColor(255, 255, 255, 255, 255, 255);
                  let page = _this.activeMarkupPage;

                  var rotation = _this.activeMarkupPage?.rotation;
                  if (rotation) {
                    rotateViewerCamera(_this.markupViewer, rotation);
                  }

                  // remove previous links if any...
                  $(_this.markupViewer.container).find('.markup-link-popup').remove();
                  _this.removeImportBoxElements();
                  if (page && page.links && page.links.length > 0) {
                    page.links.forEach((link) => {
                      _this.renderLinkOnViewer(link);
                    });
                  }

                  _this.markupViewer
                    .loadExtension('Autodesk.Viewing.MarkupsCore')
                    .then(function (extension) {
                      _this.autodeskMarkupsExtension = extension;
                      _this.autodeskMarkupsExtension.addEventListener(
                        'EVENT_HISTORY_CHANGED',
                        (e) => _this.onMarkupHistoryChanged(e),
                      );
                      _this.autodeskMarkupsExtension.addEventListener(
                        'EVENT_MARKUP_SELECTED',
                        (e) => _this.onMarkupSelect(e),
                      );
                      _this.autodeskMarkupsExtension.addEventListener(
                        'EVENT_MARKUP_DESELECT',
                        (e) => _this.onMarkupDeselect(e),
                      );
                      _this.autodeskMarkupsExtension.addEventListener(
                        'EVENT_HISTORY_CHANGED',
                        (e) => _this.onHistoryChanged(e),
                      );
                      _this.autodeskMarkupsExtension.addEventListener('EVENT_EDITMODE_LEAVE', (e) =>
                        _this.onEditModeLeave(),
                      );

                      $('html').keydown(function (e) {
                        if (e.keyCode == 46) {
                          _this.onDeleteKeyPressed(e);
                        }
                      });
                      //Enable stamp markup
                      var rootPath = appConfig.debugMode ? '../../' : appConfig.UI_URL;

                      require(`@/extensions/StampExtension.js`);

                      let ext = _this.markupViewer.getExtension('StampExtension');

                      if (ext) {
                        _this.stampsExtension = ext;
                        _this.loadMarkup(_this.attachment);
                      } else {
                        _this.markupViewer
                          .loadExtension('StampExtension')
                          .then(function (stampExt) {
                            _this.stampsExtension = stampExt;
                            _this.loadMarkup(_this.attachment);
                          });
                      }

                      //crop
                      let croppingEx = _this.markupViewer.getExtension('Cropping2DExtension');
                      let page = _this.activeMarkupPage;
                      if (page && page.cropBox) {
                        croppingEx?.setCropBox(page.cropBox);
                      }
                    });
                },
              );
            });
        });
      },
      getAttachmentPagesCount() {
        var _this = this;
        var pagesCount = getViewerCurrentPdfPagesCount(_this.markupViewer);
        _this.$store.commit('setActiveAttachmentPagesCount', pagesCount);
        return pagesCount;
      },
      //if document already opened and we want to open another page
      async openPageByNumber(pageNumber) {
        var _this = this;
        unloadViewerAllModels(_this.markupViewer);

        if (!pageNumber || isNaN(pageNumber) || pageNumber < 1) {
          return;
        }

        _this.currentPageNumber = pageNumber / 1;
        _this.$store.commit('setActiveAttachmentPage', pageNumber);

        var pdfUrl = await fetchDocumentAsObjectUrl(_this.attachment);
        if (!pdfUrl || !_this.markupViewer) {
          return;
        }

        var attachment = _this.attachment;
        if (attachment.pagesMarkups) {
          let page = attachment.pagesMarkups.find((o) => o.pageId / 1 == pageNumber / 1);
          if (!page) {
            page = new ForgePageMarkup();
            page.pageId = _this.currentPageNumber / 1;
            _this.attachment.pagesMarkups.push(page);
          }
          _this.activeMarkupPage = page;
        }
        var url = _this.isMarkupImage ? getEmptyFileUrl() : pdfUrl;
        _this.markupViewer.loadModel(
          url,
          {
            page: pageNumber,
          },
          (doc) => {
            //For Flutter app
            if (_this.isFlutterRunning) onMarkupViewerLoaded();

            //draw image on plane if image
            if (_this.isMarkupImage) drawImageOnPlane(pdfUrl, _this.markupViewer);

            var rotation = _this.activeMarkupPage?.rotation;
            if (rotation) {
              rotateViewerCamera(_this.markupViewer, rotation);
            }
            _this.loadMarkup(_this.attachment);
          },
        );
      },
      loadMarkup(attachment) {
        var _this = this;
        let markupJson = {};
        if (attachment.pagesMarkups) {
          let page = attachment.pagesMarkups.find(
            (o) => o.pageId / 1 == _this.currentPageNumber / 1,
          );
          if (page && page.markupText) {
            markupJson = JSON.parse(page.markupText);
          }

          // remove previous links if any...
          $(_this.markupViewer.container).find('.markup-link-popup').remove();
          _this.removeImportBoxElements();

          if (page && page.links && page.links.length > 0) {
            page.links.forEach((link) => {
              _this.renderLinkOnViewer(link);
            });
          }

          $(_this.markupViewer.container).find('.markup-comment').remove();
          if (page?.comments?.length) {
            _this.updateMarkupsComments(page);
          }
        }
        if (Object.keys(markupJson).length === 0) {
          return;
        }
        setTimeout(() => {
          _this.restoreMarkupsFromJosn(markupJson);
        }, 500);
      },
      startMarkupTool(toolId, layerName) {
        var _this = this;
        var avmu = Autodesk.Viewing.Extensions.Markups.Core.Utils;
        var markupExtension = _this.autodeskMarkupsExtension;
        var style = avmu.cloneStyle(_this.currentMarkupsStyle);
        if (!_this.markupViewer) return;
        if (_this.isCropping) {
          _this.isCropping = false;
          _this.markupViewer.getExtension('Cropping2DExtension').setCropping(null, false);
        }

        //Just to create a new markup layer with LayerName
        markupExtension.enterEditMode(); //Enforce Creation of ext.editModeSvgLayerNode to nest new layer in
        if (!markupExtension.svgLayersMap[layerName]) {
          //Is layer existing?
          markupExtension.leaveEditMode(); //Leave edit mode to be able to do loadMarkups
          markupExtension.loadMarkups('', layerName); //Enforce create new markups layer
        }
        markupExtension.enterEditMode(layerName);

        let tool = '';

        switch (toolId) {
          case 'arrow':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeArrow(markupExtension);
            break;
          case 'rectangle':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeRectangle(markupExtension);
            break;
          case 'circle':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeCircle(markupExtension);
            break;
          case 'polyline':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModePolyline(markupExtension);
            break;
          case 'line':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModePolyline(markupExtension);
            //Overwrite built-in onMouseDown to stop after two points only.
            tool.onMouseDown = function (event) {
              Autodesk.Extensions.Markup.Core.EditMode.prototype.onMouseDown.call(tool);
              // User selected an already created markup.
              if (tool.selectedMarkup && !tool.creating) {
                return;
              }
              if (tool.creating) {
                if (
                  this.$store.state.MARKUPS.markupActiveTool == 'line' &&
                  tool.movements.length > 1
                ) {
                  tool.creationEnd();
                }
                return;
              }
              // Creation process.
              var editor = tool.editor;
              var mousePosition = editor.getMousePosition();
              mousePosition = editor.clientToMarkups(mousePosition.x, mousePosition.y);
              var size = (tool.size = editor.sizeFromClientToMarkups(1, 1));
              tool.movements = [mousePosition, mousePosition];
              editor.beginActionGroup();
              var markupId = editor.getId();
              var create = new Autodesk.Extensions.Markup.Core.CreatePolyline(
                editor,
                markupId,
                mousePosition,
                size,
                0,
                [{ x: 0, y: 0 }],
                tool.style,
              );
              create.execute();
              tool.selectedMarkup = editor.getMarkup(markupId);
              tool.creationBegin();
            };
            break;
          case 'text':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeText(markupExtension);
            tool.__proto__.onMouseDown = () => _this.onTextToolMouseDown(tool);
            break;
          case 'cloud':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeCloud(markupExtension);
            break;
          case 'highlight':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeHighlight(markupExtension);
            style['stroke-opacity'] = 0.5;
            break;
          case 'stamp':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeStamp(markupExtension);
            break;
          case 'callout':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeCallout(markupExtension);
            break;
          case 'dimension':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeDimension(markupExtension);
            break;
          case 'pen':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModePen(markupExtension);
            break;
          case 'polycloud':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModePolycloud(markupExtension);
            break;
          case 'freehand':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModeFreehand(markupExtension);
            break;
          case 'mask':
            tool = new Autodesk.Viewing.Extensions.Markups.Core.EditModePolyline(markupExtension);
            style['stroke-width'] = 0.02; //Clear Mask Boundary thickness to be 0.02
            style['stroke-opacity'] = 0.5; //Mask boundary opacity to be 0.5
            style['fill-opacity'] = 1; //Make polygon filled
            style['fill-color'] = '#ffffff'; //Make fill in white to mask whatever behinde
            break;
          default:
            return;
        }

        markupExtension.changeEditMode(tool);
        markupExtension.setStyle(style);
        _this.bottomTools.find((x) => x.id == 'redo').disabled = _this.isRedoStackEmpty;
        _this.bottomTools.find((x) => x.id == 'undo').disabled = _this.isUndoStackEmpty;
      },
      onTextToolMouseDown(tool) {
        if (tool.textInputHelper && tool.textInputHelper.isActive()) {
          tool.textInputHelper.acceptAndExit();
          return;
        }

        if (tool.selectedMarkup) {
          return;
        }

        var editor = tool.editor;
        var mousePosition = editor.getMousePosition();
        var clientFontSize = editor.sizeFromMarkupsToClient(0, tool.style['font-size']).y;
        var initialWidth = clientFontSize * 8;
        var initialHeight = clientFontSize * 1;

        // Center position.
        var size = (tool.size = editor.sizeFromClientToMarkups(initialWidth, initialHeight));
        var position = editor.positionFromClientToMarkups(
          mousePosition.x + initialWidth * 0.5,
          mousePosition.y + initialHeight * 0.5,
        );

        tool.creationBegin();
        editor.beginActionGroup();

        // Given the initial width and font size, we assume that the text fits in one line.
        var createText = new Autodesk.Viewing.Extensions.Markups.Core.CreateText(
          editor,
          editor.getId(),
          position,
          size,
          '',
          tool.style,
        );

        createText.execute();
        tool.creationEnd();

        tool.selectedMarkup = editor.getMarkup(createText.targetId);
        tool.textInputHelper && tool.textInputHelper.setActive(tool.selectedMarkup, true);
        tool.editor.actionManager.addEventListener(
          Autodesk.Viewing.Extensions.Markups.Core.MarkupEvents.EVENT_HISTORY_CHANGED,
          tool.onHistoryChangeBinded,
        );
      },
      downloadMarkup(item) {
        this.forgeService.downloadMarkup(item);
      },
      updateDeleteButtonPosition() {
        var _this = this;
        if (!_this.selectedMarkup || !_this.selectedMarkup.shape) {
          return;
        }
        var shape = _this.selectedMarkup.shape;
        var boundingBox = $(shape)[0].getBoundingClientRect();
        if (!boundingBox) {
          return;
        }
        var deleteButton = $('.markup-delete-floating-button');
        deleteButton.attr(
          'data-x',
          _this.selectedMarkup.position.x + _this.selectedMarkup.size.x / 2,
        );
        deleteButton.attr(
          'data-y',
          _this.selectedMarkup.position.y + _this.selectedMarkup.size.y / 2,
        );
        this.forgeService.updateLabelsPositionsByClassName(
          'markup-delete-floating-button',
          _this.markupViewer,
        );
      },
      onMarkupHistoryChanged(e) {
        var _this = this;
        if (e.type !== 'EVENT_HISTORY_CHANGED' || !_this.selectedMarkup) return;
        _this.updateDeleteButtonPosition();
      },
      onMarkupSelect(e) {
        var _this = this;
        if (e.type != 'EVENT_MARKUP_SELECTED') {
          return;
        }
        _this.isMarkupSelected = true;
        _this.selectedMarkup = e.markup;
        _this.updateDeleteButtonPosition();
      },
      onMarkupDeselect(e) {
        var _this = this;
        if (e.type != 'EVENT_MARKUP_DESELECT') {
          return;
        }
        _this.isMarkupSelected = false;
        _this.selectedMarkup = null;
      },
      onDeleteKeyPressed(e) {
        var _this = this;
        var markup = _this.selectedMarkup;
        var markupExtension = _this.autodeskMarkupsExtension;
        if (!markupExtension || !markupExtension.editMode || !markup) {
          return;
        }

        var testBlindMarkup;
        var testBlindMarkups = _this.$store.state.MARKUPS.testBlindMarkups;
        if (Array.isArray(testBlindMarkups)) {
          testBlindMarkup = testBlindMarkups.find(
            (o) => o?.ForgeMarkupID?.toLowerCase() == markup.id?.toLowerCase(),
          );
          let isBeingDeleted = _this.$store.state.MARKUPS.testBlindsToBeDeleted.find(
            (o) => o?.ForgeMarkupID?.toLowerCase() == markup.id?.toLowerCase(),
          );
          if (testBlindMarkup && !isBeingDeleted) {
            _this.$store.state.MARKUPS.testBlindsToBeDeleted.push(testBlindMarkup);
          }
        }

        var testBlindMarkupsToBeAdded = _this.$store.state.MARKUPS.testBlindsToBeAdded;
        if (Array.isArray(testBlindMarkupsToBeAdded)) {
          testBlindMarkup = testBlindMarkupsToBeAdded.find(
            (o) => o?.ForgeMarkupID?.toLowerCase() == markup.id?.toLowerCase(),
          );
          if (testBlindMarkup) {
            testBlindMarkupsToBeAdded.pop(testBlindMarkup);
          }
        }
        _this.selectedMarkup = null;
      },
      onMarkupDelete() {
        var _this = this;
        var markupExtension = _this.autodeskMarkupsExtension;
        var selection = markupExtension.getSelection();
        if (selection) {
          markupExtension.deleteMarkup(selection);
          var testBlindMarkup;
          var testBlindMarkups = _this.$store.state.MARKUPS.testBlindMarkups;
          if (Array.isArray(testBlindMarkups)) {
            testBlindMarkup = testBlindMarkups.find(
              (o) => o?.ForgeMarkupID?.toLowerCase() == selection.id?.toLowerCase(),
            );
            if (testBlindMarkup) {
              _this.$store.state.MARKUPS.testBlindsToBeDeleted.push(testBlindMarkup);
            }
          }
          var testBlindMarkupsToBeAdded = _this.$store.state.MARKUPS.testBlindsToBeAdded;
          if (Array.isArray(testBlindMarkupsToBeAdded)) {
            testBlindMarkup = testBlindMarkupsToBeAdded.find(
              (o) => o?.ForgeMarkupID?.toLowerCase() == selection.id?.toLowerCase(),
            );
            if (testBlindMarkup) {
              _this.$store.state.MARKUPS.testBlindsToBeAdded.pop(testBlindMarkup);
            }
          }
        }

        var previousActiveTool = this.$store.getters['getMarkupActiveTool'];
        var targetToolId = 'highlight';

        if (previousActiveTool !== targetToolId) {
          _this.onMarkupToolClicked({
            id: targetToolId,
          });
        }
      },
      onUndo() {
        var markupExtension = this.autodeskMarkupsExtension;
        if (!markupExtension.isUndoStackEmpty()) {
          this.autodeskMarkupsExtension.undo();
        }
        this.bottomTools.find((x) => x.id == 'redo').disabled = this.isRedoStackEmpty;
        this.bottomTools.find((x) => x.id == 'undo').disabled = this.isUndoStackEmpty;
        this.setActiveTool('');
      },
      onRedo() {
        var markupExtension = this.autodeskMarkupsExtension;
        if (!markupExtension.isRedoStackEmpty()) {
          this.autodeskMarkupsExtension.redo();
        }
        this.bottomTools.find((x) => x.id == 'redo').disabled = this.isRedoStackEmpty;
        this.bottomTools.find((x) => x.id == 'undo').disabled = this.isUndoStackEmpty;
        this.setActiveTool('');
      },
      onHistoryChanged(e) {
        var _this = this;
        _this.bottomTools.find((x) => x.id == 'redo').disabled = _this.isRedoStackEmpty;
        _this.bottomTools.find((x) => x.id == 'undo').disabled = _this.isUndoStackEmpty;
        if (_this.isFlutterRunning) {
          try {
            var historyStack = {
              redo: _this.isRedoStackEmpty,
              undo: _this.isUndoStackEmpty,
            };
            onHistoryChangedFlutter(JSON.stringify(historyStack));
          } catch (error) {
            error(error);
          }
        }
      },
      onSave(hidePopup = false, callback) {
        var _this = this;
        var saveConfirmed = false;
        // _this.popupMessageType = 'save';
        // _this.popupMessageText = 'Do you want to save changes?';

        _this.popupMessageCallback = async function (confirmed) {
          _this.popupMessageType = '';
          saveConfirmed = confirmed;
          _this.setActiveTool('');
          if (!saveConfirmed) {
            if (callback) {
              await callback(saveConfirmed);
            }
            return false;
          }

          //Clear Markups history upon saving
          _this.clearMarkupsHistory();

          //Show save loading screen
          if (!_this.isFlutterRunning) _this.isMarkupsSaving = true;

          //adjust markups viewer to fit view, in order to take markups snapshot
          _this.markupViewer.navigation.FIT_TO_VIEW_VERTICAL_MARGIN = 0;
          _this.markupViewer.navigation.FIT_TO_VIEW_HORIZONTAL_MARGIN = 0;
          var modelBBox = _this.markupViewer.model.getBoundingBox();
          var modelWidth = Math.abs(modelBBox.max.x - modelBBox.min.x);
          var modelHeight = Math.abs(modelBBox.max.y - modelBBox.min.y);
          var boundingBoxCleintSize = _this.autodeskMarkupsExtension.sizeFromMarkupsToClient(
            modelWidth,
            modelHeight,
          );
          _this.markupViewer.clientContainer.style.width = boundingBoxCleintSize.x + 'px';
          if (_this.isMarkupImage) {
            let min = new THREE.Vector3(0, 0, 0);
            let max = new THREE.Vector3(
              this.$store.state.markupImageWidth,
              this.$store.state.markupImageHeight,
              0,
            );
            var box = new THREE.Box3(min, max);
            _this.markupViewer.navigation.fitBounds(true, box, false, true);
          } else _this.markupViewer.navigation.fitBounds(true, modelBBox, false, true);

          //01. Cloned version of attachment
          if (!_this.attachment.pagesMarkups) _this.attachment.pagesMarkups = [];
          let page = _this.attachment.pagesMarkups.find(
            (o) => o.pageId / 1 == _this.currentPageNumber / 1,
          );
          if (!page) {
            page = new ForgePageMarkup();
            page.pageId = _this.currentPageNumber / 1;
            _this.attachment.pagesMarkups.push(page);
          }

          var markupsForPrint = [];

            // _this.attachment.referenceDocumentId=_this.selectedLinkDrawing.id


          page.cropBox = _this.markupViewer?.getExtension('Cropping2DExtension')?.cropBox;
          var markupJson = _this.generateJsonFromMarkups();
          page.markupText = JSON.stringify(markupJson);

          if (_this.autodeskMarkupsExtension.editModeSvgLayerNode) {
            page.svg = _this.autodeskMarkupsExtension.generateData();
          }

          if (_this.$store.state.pdfviewer) {
            updateMarkupsOnViewer(_this.$store.state.pdfviewer, markupJson, 'Layer_1');
          }

          exportMarkupImage(_this.markupViewer, async function (ImageBase64) {
            page.ImageBase64 = ImageBase64;

            // Remove links selected for unlinking before saving
            _this.removeSelectedLinksFromPageMarkup();

            saveAttachmentToDatabase(_this.attachment);

            // Clear importing flags from saved links - they're now permanent
            _this.clearImportingFlags();

            if (callback) {
              await callback(saveConfirmed);
            }

            _this.isMarkupsSaving = false;
            if (_this.markupViewer) {
              _this.markupViewer.clientContainer.style.width = 'calc(100% - 300px)';
              _this.markupViewer.resize();

              if (isParentOrigin()) {
                deletePrintMarkups(_this.autodeskMarkupsExtension, markupsForPrint);
              }
            }
          });

          //wait for addNewBlindMarkupsToOrigin() from testBlindUtils.js
          if (hidePopup) {
            setTimeout(() => {
              if (_this.markupViewer) {
                _this.isMarkupsSaving = false;
                _this.markupViewer.finish();
                _this.markupViewer = null;
              }
              _this.hidePopup();
            }, 200);
          }
          if (_this.isFlutterRunning) {
            onSaveFlutter();
          }
        };

        this.$dialog.confirm({
          text: 'Do you want to save changes?',
          title: 'Save Changes',
          actions: {
            false: {
              text: 'Cancel',
              color: 'error',
              handle: () => {
                _this.popupMessageCallback(false);
              },
            },
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                _this.popupMessageCallback(true);
              },
            },
          },
        });
      },
      onDiscard() {
        var _this = this;
        var discardConfirmed = false;
        _this.popupMessageText = 'Do you want to discard changes?';

        _this.popupMessageCallback = function (confirmed) {
          _this.popupMessageType = '';
          discardConfirmed = confirmed;
          _this.setActiveTool('');
          if (!discardConfirmed) {
            return;
          }
          // Remove any imported links that weren't saved
          _this.removeImportedLinks();
          _this.onHideToolbar();
          _this.hidePopup();
        };

        this.$dialog.confirm({
          text: 'Do you want to discard changes?',
          title: 'Discard Changes',
          actions: {
            false: {
              text: 'No',
              color: 'error',
              handle: () => {
                _this.popupMessageCallback(false);
              },
            },
            true: {
              text: 'Yes',
              color: 'primary',
              handle: () => {
                _this.popupMessageCallback(true);
              },
            },
          },
        });
      },
      onFormatClick() {
        $('#markups-format-popup').toggle(200);
      },
      onStampsClick() {
        $('.stamps-popup').toggle(200);
      },
      generateJsonFromMarkups() {
        var _this = this;
        if (!_this.autodeskMarkupsExtension) {
          return [];
        }
        _this.autodeskMarkupsExtension.leaveEditMode();
        var all = _this.autodeskMarkupsExtension.markups;
        var allMarkups = [];
        for (var i = 0; i < all.length; i++) {
          var item = all[i];
          if (!item) continue;
          if (allMarkups.some((q) => q.id == item.id)) continue;
          allMarkups.push(item);
        }
        var customMarkupsArrToBeSaved = [];
        for (let i = 0; i < allMarkups.length; i++) {
          var markup = allMarkups[i];
          if (markup.isTemp) {
            continue;
          }
          if (markup.type == 'stamp' && markup.customSVG == '') {
            continue;
          }
          var customObj = {};
          var newGuid = this.forgeService.newGuid();
          customObj.type = markup.type;
          customObj.id = isNaN(markup.id) ? markup.id : newGuid;
          customObj.position = markup.position;
          customObj.size = markup.size;
          customObj.rotation = markup.rotation;
          customObj.style = markup.style;
          customObj.locations = markup.locations; //not applicable to stamp
          customObj.closed = markup.closed; //not applicable to stamp
          customObj.head = markup.head; //not applicable to stamp
          customObj.tail = markup.tail; //not applicable to stamp
          customObj.text = markup.currentText; //not applicable to stamp
          //stamp
          if (markup.customSVG) {
            customObj.svg = Autodesk.Viewing.Extensions.Markups.Core.Utils.svgNodeToString(
              markup.customSVG,
            );
          }
          customMarkupsArrToBeSaved.push(customObj);
        }
        return customMarkupsArrToBeSaved;
      },
      restoreMarkupsFromJosn(jsonData, layerName = 'Layer_1') {
        var _this = this;
        if (!jsonData) return;
        //Just to create a new markup layer with LayerName
        _this.autodeskMarkupsExtension.enterEditMode(); //Enforce Creation of ext.editModeSvgLayerNode to nest new layer in
        _this.autodeskMarkupsExtension.leaveEditMode(); //Leave edit mode to allow creation of new layer
        _this.autodeskMarkupsExtension.loadMarkups('', layerName); //Enforce create new markups layer

        _this.autodeskMarkupsExtension.enterEditMode(layerName);
        _this.autodeskMarkupsExtension.clear();
        _this.autodeskMarkupsExtension.leaveEditMode();

        var editor = _this.autodeskMarkupsExtension;
        var avemc = Autodesk.Viewing.Extensions.Markups.Core;
        for (var i = 0; i < jsonData.length; i++) {
          var currentMarkup = jsonData[i];
          var createMarkup = null;
          var id = currentMarkup.id;
          var tail = currentMarkup.tail;
          var head = currentMarkup.head;
          var style = currentMarkup.style;
          var size = currentMarkup.size;
          var text = currentMarkup.text;
          var position = currentMarkup.position;
          var rotation = currentMarkup.rotation;
          var closed = currentMarkup.closed;
          var locations = currentMarkup.locations;

          switch (jsonData[i].type) {
            case 'arrow':
              createMarkup = new avemc.CreateArrow(editor, id, tail, head, style);
              break;
            case 'label':
              createMarkup = new avemc.CreateText(editor, id, position, size, text, style);
              break;
            case 'ellipse':
              createMarkup = new avemc.CreateCircle(editor, id, position, size, rotation, style);
              break;
            case 'cloud':
              createMarkup = new avemc.CreateCloud(editor, id, position, size, rotation, style);
              break;
            case 'callout':
              createMarkup = new avemc.CreateCallout(editor, id, position, size, rotation, style);
              break;
            case 'rectangle':
              createMarkup = new avemc.CreateRectangle(editor, id, position, size, rotation, style);
              break;
            case 'freehand':
              createMarkup = new avemc.CreateFreehand(
                editor,
                id,
                position,
                size,
                rotation,
                locations,
                style,
              );
              break;
            case 'highlight':
              createMarkup = new avemc.CreateHighlight(
                editor,
                id,
                position,
                size,
                rotation,
                locations,
                style,
              );
              break;
            case 'polyline':
              createMarkup = new avemc.CreatePolyline(
                editor,
                id,
                position,
                size,
                rotation,
                locations,
                style,
                closed,
              );
              break;
            case 'polycloud':
              createMarkup = new avemc.CreatePolycloud(
                editor,
                id,
                position,
                size,
                rotation,
                locations,
                style,
                closed,
              );
              break;
            case 'stamp':
              var customSVG = avemc.Utils.stringToSvgNode(currentMarkup.svg);
              //If Test Blind Markup just check it's sequence
              var testBlindMarkup;
              var testBlindMarkups = _this.$store.state.MARKUPS.testBlindMarkups;
              if (Array.isArray(testBlindMarkups)) {
                testBlindMarkup = testBlindMarkups.find(
                  (o) => o?.ForgeMarkupID?.toLowerCase() == id?.toLowerCase(),
                );
                if (testBlindMarkup) {
                  var sequence = testBlindMarkup.Sequence;
                  sequence = pad(sequence, 3);
                  var innerText = customSVG.querySelector('#stamp-text-lower');
                  if (innerText) {
                    innerText.innerHTML = sequence;
                  }
                }
              }

              var { StampCreateAction } = require('../../extensions/StampExtension');

              createMarkup = new StampCreateAction(
                editor,
                id,
                position,
                size,
                rotation,
                style,
                customSVG,
              );
              break;
          }
          if (createMarkup) {
            createMarkup.addToHistory = false;
            _this.autodeskMarkupsExtension.enterEditMode(layerName);
            createMarkup.execute();
          }
        }
        //force clear selection
        _this.autodeskMarkupsExtension.selectMarkup();
        _this.autodeskMarkupsExtension.leaveEditMode();
        _this.clearMarkupsHistory();
      },
      clearMarkupsHistory() {
        var _this = this;
        if (_this.autodeskMarkupsExtension && _this.autodeskMarkupsExtension.actionManager) {
          _this.autodeskMarkupsExtension.actionManager.undoStack = [];
          _this.autodeskMarkupsExtension.actionManager.redoStack = [];
        }
      },
      updateMarkupsComments(page) {
        // remove previous comments
        $(this.markupViewer.container).find('.markup-comment').remove();
        if (!page?.comments?.length) return;
        const COMMENT_CLASS = 'markup-comment';
        // create new comments
        page.comments.forEach((comment) => {
          var iconOptions = {
            position: comment.position,
            width: comment.width,
            height: comment.height,
            rotation: comment.rotation,
            differences: null,
            title: comment.createdBy,
            id: comment.id,
            className: COMMENT_CLASS,
            forgeViewer: this.markupViewer,
             linkType:link.type,
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
              this.$store.commit('SET_IS_COMMENTS_PANEL_ACTIVE', true);
              this.$store.commit('SET_SELECTED_COMMENT', comment.id);
            },
            addContent: false,
            removeTildeSign: false,
          };
          this.forgeService.create3DIconToViewer(iconOptions);
        });
        this.forgeService.updateLabelsPositionsByClassName(COMMENT_CLASS, this.markupViewer);
      },
      resizeOnTime(time) {
        setTimeout(() => {
          if (this.markupViewer) {
            this.markupViewer.resize();
          }
        }, time);
      },
    },
    computed: {
      forgeService: {
        get() {
          return this.$store.state.forgeService;
        },
      },
      isFullscreen: function () {
        var status = this.$store.state.MARKUPS.markupsPopupStatus;
        return status?.toLowerCase() == PopupStatus.FULLSCREEN;
      },
      isMinimized: function () {
        var status = this.$store.state.MARKUPS.markupsPopupStatus;
        return status?.toLowerCase() == PopupStatus.MINIMIZED;
      },
      isActive: function () {
        var status = this.$store.state.MARKUPS.markupsPopupStatus;
        var isActive = status?.toLowerCase() == PopupStatus.ACTIVE;
        if (isActive) {
          this.resizeOnTime(3000);
        }
        return isActive;
      },
      isUndefined: function () {
        var status = this.$store.state.MARKUPS.markupsPopupStatus;
        return status?.toLowerCase() == PopupStatus.UNDEFINED;
      },
      activeTool: function () {
        return this.$store.getters['getMarkupActiveTool'];
      },
      isFormatPopupVisible: function () {
        return this.$store.getters['getMarkupActiveTool'] === 'format';
      },
      isStampsPopupVisible: function () {
        return $('.stamps-popup').css('display') != 'none';
      },
      isShapesToolVisible: function () {
        return this.$store.getters['getMarkupActiveTool'] === 'shapes';
      },
      isEditModeActive: function () {
        return this.autodeskMarkupsExtension?.duringEditMode;
      },
      isCommentsPanelActive: {
        set: function (value) {
          this.$store.commit('SET_IS_COMMENTS_PANEL_ACTIVE', value);
        },
        get: function () {
          return this.$store.getters['GET_IS_COMMENTS_PANEL_ACTIVE'];
        },
      },
      autoTags() {
        return this.$store.getters['GET_EXISTING_AUTOMATIC_TAGS_INFO'];
      },
      linkDisplayMode() {
        return this.$store.state.MARKUPS.linkDisplayMode;
      },

      isFlutterRunning: function () {
        return this.$store.getters['getIsFlutterRunning'];
      },
      isRedoStackEmpty: function () {
        return this.autodeskMarkupsExtension?.isRedoStackEmpty();
      },
      isUndoStackEmpty: function () {
        return this.autodeskMarkupsExtension?.isUndoStackEmpty();
      },
      isMarkupImage: function () {
        return this.$store.state.isMarkupImage;
      },
      isRightMenuActive: function () {
        return this.activeTool === 'relatedDocuments' || this.activeTool === 'thumbnails';
      },
      viewerWidth: function () {
        // New layout: viewer takes remaining space after right panel
        return 'calc(100% - 320px)';
      },
      totalPages: function () {
        return this.$store.state.MARKUPS.activeAttachmentPagesCount || 0;
      },
      hasLinks: function () {
        if (!this.activeMarkupPage || !this.activeMarkupPage.links) {
          return false;
        }
        return this.activeMarkupPage.links.length > 0;
      },
      linksCount: function () {
        if (!this.activeMarkupPage || !this.activeMarkupPage.links) {
          return 0;
        }
        return this.activeMarkupPage.links.length;
      },
      commentsCount: function () {
        if (!this.activeMarkupPage || !this.activeMarkupPage.comments) {
          return 0;
        }
        return this.activeMarkupPage.comments.length;
      },
      currentHighlightedTagsFromViewers: function () {
        let data3d = this.$store.getters.get3dThumbnailPopupInfo;
        let data2d = this.$store.getters.get2dThumbnailPopupInfo;
        let data360 = this.$store.getters.get360ThumbnailPopupInfo;

        let tags = [];

        if (data3d.shown) {
          tags = [...(data3d?.items ?? [])];

          if (data3d?.item) {
            tags.push(data3d.item);
          }
        }

        if (data2d.shown) {
          tags = [...(data2d?.items ?? [])];

          if (data2d?.item) {
            tags.push(data2d.item);
          }
        }

        if (data360.shown) {
          tags = [...(data360?.items ?? [])];

          if (data360?.item) {
            tags.push(data360.item);
          }
        }

        let validIds = tags
          .filter((t) => t instanceof ForgeEquipment || t instanceof ForgePipeline)
          .map((t) => t.id);

        return validIds;
      },
    },
    watch: {
      linkDisplayMode() {
        // Re-render all visible links when display mode toggles
        this.refreshLinkDisplay();
      },
      isMinimized: function () {
        var status = this.$store.state.MARKUPS.markupsPopupStatus;
        if (status == PopupStatus.ACTIVE || status == PopupStatus.FULLSCREEN) {
          setTimeout(() => {
            if (this.markupViewer) {
              this.markupViewer.resize();
            }
          }, 1000);
        } else {
          this.setActiveTool('');
        }
      },
      viewerWidth: function (oldWidth, newWidth) {
        if (this.markupViewer && oldWidth !== newWidth) {
          setTimeout(() => this.markupViewer.resize(), 1000);
        }
      },
      currentHighlightedTagsFromViewers: function (newIds) {
        const markupLinks = document.querySelectorAll('.markup-link');
        markupLinks.forEach((link) => {
          const id = link.getAttribute('data-metadata');
          if (newIds.some((v) => v == id)) {
            if (!link.classList.contains('highlighted-markup-link')) {
              link.classList.add('highlighted-markup-link');
            }
          } else {
            link.classList.remove('highlighted-markup-link');
          }
        });
      },
    },
  };
</script>

<style scoped>
  /* ================================
     MARKUPS POPUP - MAIN CONTAINER
     ================================ */
  .markups-popup {
    height: 90%;
    width: 85%;
    left: calc(50% - 85% / 2);
    display: none;
    flex-direction: column;
    background: #ffffff;
    color: #333;
    padding: 0;
    position: absolute;
    overflow: hidden;
    animation-name: markup-animation;
    animation-duration: 0.4s;
    z-index: 10;
    resize: both;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .markups-popup.fullscreen {
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    resize: none;
    border-radius: 0;
    animation-name: none;
  }

  .minimized {
    animation-name: markup-minimize-animation;
    animation-timing-function: ease-in-out;
    animation-duration: 0.5s;
    animation-fill-mode: forwards;
  }

  @keyframes markup-animation {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes markup-minimize-animation {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.95) translate(calc(100vw - 565px), 0px);
    }
  }

  /* ================================
     HEADER BAR
     ================================ */
  .markup-header {
    display: flex;
    align-items: center;
    min-height: 48px;
    padding: 0 16px;
    cursor: move;
    user-select: none;
    z-index: 100;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: linear-gradient(to bottom, #fafafa, #f5f5f5);
  }

  .markup-header p {
    color: #333;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.2px;
  }

  .markups-popup.fullscreen > .markup-header {
    display: none;
  }

  .markup-header-btn-group {
    display: flex;
    gap: 4px;
  }

  .markup-header-btn-group .v-btn {
    color: #666;
  }

  .markup-header-btn-group .v-btn:hover {
    color: var(--v-primary-base);
    background: rgba(var(--v-primary-base), 0.08);
  }

  /* ================================
     TOP TOOLBAR
     ================================ */
  .markup-top-toolbar {
    display: flex;
    align-items: center;
    padding: 8px 16px;
    gap: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    background: #fafafa;
    min-height: 48px;
  }

  .toolbar-section {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .toolbar-section .v-btn {
    border-radius: 6px;
    transition: all 0.15s ease;
  }

  .toolbar-section .v-btn:hover {
    background: rgba(var(--v-primary-base), 0.08);
  }

  .toolbar-section .v-btn.tool-active {
    background: var(--v-primary-base);
    color: white;
  }

  .toolbar-section .v-btn.tool-active .v-icon {
    color: white !important;
  }

  .toolbar-section .v-btn:disabled {
    opacity: 0.4;
  }

  .shapes-dropdown-btn {
    border-radius: 6px;
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
  }

  .shapes-dropdown-btn.tool-active {
    background: var(--v-primary-base);
    color: white;
  }

  .shapes-dropdown-list {
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }

  .shapes-dropdown-list .v-list-item {
    min-height: 40px;
  }

  .shapes-dropdown-list .v-list-item--active {
    background: rgba(var(--v-primary-base), 0.08);
  }

  /* ================================
     MAIN CONTENT AREA
     ================================ */
  .markup-main-content {
    display: flex;
    flex-direction: row;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    background: #f0f0f0;
  }

  #markup-viewer {
    position: relative;
    flex-grow: 1;
    background: #e8e8e8;
    border-radius: 0;
  }

  #markup-viewer :deep(#guiviewer3d-toolbar) {
    display: none;
  }

  /* ================================
     LEFT SIDE PANELS
     ================================ */
  .markup-left-panels {
    width: 320px;
    min-width: 320px;
    background: #ffffff;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .collapsible-panels-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
  }

  .collapsible-panel {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .collapsible-panel:last-child {
    border-bottom: none;
  }

  /* When panel is expanded and other is collapsed - take full height */
  .collapsible-panel.panel-expanded-full {
    flex: 1;
  }

  .collapsible-panel.panel-expanded-full .panel-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .collapsible-panel.panel-expanded-full .panel-content-scroll {
    max-height: none;
    flex: 1;
    overflow-y: auto;
  }

  .panel-header {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;
    background: #fafafa;
    transition: background 0.15s ease;
  }

  .panel-header:hover {
    background: #f0f0f0;
  }

  .panel-title {
    font-size: 13px;
    font-weight: 600;
    color: #333;
    letter-spacing: -0.1px;
  }

  .panel-body {
    background: #ffffff;
  }

  .panel-content-scroll {
    max-height: 300px;
    overflow-y: auto;
    padding: 12px 16px;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    text-align: center;
  }

  .empty-state p {
    font-size: 13px;
  }

  /* ================================
     BOTTOM SECTION (Thumbnails + Navigator + Action Bar)
     ================================ */
  .markup-bottom-section {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    background: #ffffff;
  }

  /* ================================
     THUMBNAIL GRID (at bottom)
     ================================ */
  .thumbnail-grid-container {
    padding: 12px 16px;
    background: #f5f5f5;
    max-height: 180px;
    overflow-x: auto;
    overflow-y: hidden;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .thumbnail-grid-container::-webkit-scrollbar {
    height: 6px;
  }

  .thumbnail-grid-container::-webkit-scrollbar-track {
    background: transparent;
  }

  .thumbnail-grid-container::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }

  .thumbnail-grid-container::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.25);
  }

  /* ================================
     BOTTOM ACTION BAR
     ================================ */
  .markup-bottom-bar {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    background: linear-gradient(to top, #fafafa, #ffffff);
    min-height: 56px;
  }

  .bottom-bar-left {
    display: flex;
    align-items: center;
  }

  .page-indicator {
    font-size: 12px;
    font-weight: 500;
    border-color: rgba(0, 0, 0, 0.15);
  }

  .bottom-bar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .action-btn {
    text-transform: none;
    font-weight: 500;
    letter-spacing: 0;
    border-radius: 6px;
  }

  .action-btn.save-btn {
    min-width: 100px;
    box-shadow: 0 2px 8px rgba(var(--v-primary-base), 0.25);
  }

  /* ================================
     SHOW TOOLS BUTTON
     ================================ */
  .show-tools-btn {
    height: 36px;
    position: absolute;
    top: 12px;
    right: 12px;
    border-radius: 8px;
    color: white;
    background-color: var(--v-primary-base);
    z-index: 1;
    padding: 8px 20px;
    text-align: center;
    box-sizing: border-box;
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    font-size: 13px;
    font-weight: 500;
    transition: all 0.15s ease;
  }

  .show-tools-btn:hover {
    background-color: var(--v-primary-darken1);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  }

  /* ================================
     TOOLBAR BUTTONS (LEGACY)
     ================================ */
  .toolbar-btn {
    background: var(--v-primary-base);
    border-radius: 8px;
    cursor: pointer;
    position: absolute;
    width: 132px;
    height: 40px;
    right: 30px;
    top: 72px;
    font-size: 14px;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  .toolbar-btn.close-btn {
    background: var(--v-primary-lighten4);
    color: var(--v-primary-base);
    display: flex;
    justify-content: center;
    top: 120px;
  }

  .toolbar-btn.close-btn:hover {
    background: var(--v-primary-lighten3);
  }

  .show-toolbar-btn:hover {
    background: var(--v-primary-darken2);
  }

  .show-toolbar-btn > .fas {
    left: 12px;
  }

  .show-toolbar-btn > span {
    left: 37px;
    font-family: var(--main-font);
  }

  /* ================================
     MARKUP ELEMENTS
     ================================ */
  .markup-delete-floating-button {
    position: absolute;
    width: 24px;
    height: 24px;
    z-index: 10;
    text-align: center;
    border-radius: 50%;
    cursor: pointer;
    font-weight: 500;
    font-size: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #ef5350;
    color: white;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
    transition: all 0.15s ease;
  }

  .markup-delete-floating-button:hover {
    background: #d32f2f;
    transform: scale(1.1);
  }

  .markup-link-draw-highlight {
    display: block;
    position: absolute;
    border-radius: 6px;
    outline: 2px solid var(--v-primary-base);
    background: rgba(var(--v-primary-base), 0.1);
    z-index: 10;
  }

  .markup-comment {
    position: absolute;
    z-index: 1;
    padding: 3px;
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    background-image: url(../../images/comment.png);
    background-size: cover;
    pointer-events: all;
    cursor: pointer;
    transition: transform 0.15s ease;
  }

  .markup-comment:hover {
    transform: scale(1.1);
  }

  .markup-link-popup,
  .markup-link {
    position: absolute;
    cursor: pointer;
    min-width: 80px;
    border-radius: 6px;
    border: 2px solid #9E9E9E;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(158, 158, 158, 0.15);
    transition: all 0.15s ease;
    box-sizing: border-box;
  }

  .markup-link-popup:hover,
  .markup-link:hover {
    background: rgba(158, 158, 158, 0.35);
  }

  /* Tag Type Colors - Annotation (blue) */
  .markup-link-popup[data-tag-type="Annotation"],
  .markup-link[data-tag-type="Annotation"] {
    border-color: #2196F3;
    background: rgba(33, 150, 243, 0.15);
  }
  .markup-link-popup[data-tag-type="Annotation"]:hover,
  .markup-link[data-tag-type="Annotation"]:hover {
    background: rgba(33, 150, 243, 0.35);
  }
  .markup-link-popup[data-tag-type="Annotation"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Annotation"] .tag-title.dimension-text {
    color: #1565C0;
  }
  .markup-link[data-tag-type="Annotation"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(33, 150, 243, 0.4);
  }

  /* Tag Type Colors - Container (purple) */
  .markup-link-popup[data-tag-type="Container"],
  .markup-link[data-tag-type="Container"] {
    border-color: #9C27B0;
    background: rgba(156, 39, 176, 0.15);
  }
  .markup-link-popup[data-tag-type="Container"]:hover,
  .markup-link[data-tag-type="Container"]:hover {
    background: rgba(156, 39, 176, 0.35);
  }
  .markup-link-popup[data-tag-type="Container"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Container"] .tag-title.dimension-text {
    color: #7B1FA2;
  }
  .markup-link[data-tag-type="Container"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(156, 39, 176, 0.4);
  }

  /* Tag Type Colors - Equipment (green) */
  .markup-link-popup[data-tag-type="Equipment"],
  .markup-link[data-tag-type="Equipment"] {
    border-color: #4CAF50;
    background: rgba(76, 175, 80, 0.15);
  }
  .markup-link-popup[data-tag-type="Equipment"]:hover,
  .markup-link[data-tag-type="Equipment"]:hover {
    background: rgba(76, 175, 80, 0.35);
  }
  .markup-link-popup[data-tag-type="Equipment"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Equipment"] .tag-title.dimension-text {
    color: #2E7D32;
  }
  .markup-link[data-tag-type="Equipment"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(76, 175, 80, 0.4);
  }

  /* Tag Type Colors - Inspection (orange) */
  .markup-link-popup[data-tag-type="Inspection"],
  .markup-link[data-tag-type="Inspection"] {
    border-color: #FF9800;
    background: rgba(255, 152, 0, 0.15);
  }
  .markup-link-popup[data-tag-type="Inspection"]:hover,
  .markup-link[data-tag-type="Inspection"]:hover {
    background: rgba(255, 152, 0, 0.35);
  }
  .markup-link-popup[data-tag-type="Inspection"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Inspection"] .tag-title.dimension-text {
    color: #E65100;
  }
  .markup-link[data-tag-type="Inspection"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(255, 152, 0, 0.4);
  }

  /* Tag Type Colors - Permit (indigo) */
  .markup-link-popup[data-tag-type="Permit"],
  .markup-link[data-tag-type="Permit"] {
    border-color: #3F51B5;
    background: rgba(63, 81, 181, 0.15);
  }
  .markup-link-popup[data-tag-type="Permit"]:hover,
  .markup-link[data-tag-type="Permit"]:hover {
    background: rgba(63, 81, 181, 0.35);
  }
  .markup-link-popup[data-tag-type="Permit"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Permit"] .tag-title.dimension-text {
    color: #283593;
  }
  .markup-link[data-tag-type="Permit"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(63, 81, 181, 0.4);
  }

  /* Tag Type Colors - Pipeline (cyan) */
  .markup-link-popup[data-tag-type="Pipeline"],
  .markup-link[data-tag-type="Pipeline"] {
    border-color: #00BCD4;
    background: rgba(0, 188, 212, 0.15);
  }
  .markup-link-popup[data-tag-type="Pipeline"]:hover,
  .markup-link[data-tag-type="Pipeline"]:hover {
    background: rgba(0, 188, 212, 0.35);
  }
  .markup-link-popup[data-tag-type="Pipeline"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Pipeline"] .tag-title.dimension-text {
    color: #00838F;
  }
  .markup-link[data-tag-type="Pipeline"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(0, 188, 212, 0.4);
  }

/* Tag Type Colors - P&ID Amber  */
.markup-link-popup[data-tag-type="P&ID"],
.markup-link[data-tag-type="P&ID"] {
  border-color: #FFC107;
  background: rgba(255, 193, 7, 0.15);
}

.markup-link-popup[data-tag-type="P&ID"]:hover,
.markup-link[data-tag-type="P&ID"]:hover {
  background: rgba(255, 193, 7, 0.35);
}

.markup-link-popup[data-tag-type="P&ID"] .tag-title.dimension-text,
.markup-link[data-tag-type="P&ID"] .tag-title.dimension-text {
  color: #FFA000;
}
  .markup-link[data-tag-type="P&ID"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.4);
  }

  /* Tag Type Colors - Scaffold (brown) */
  .markup-link-popup[data-tag-type="Scaffold"],
  .markup-link[data-tag-type="Scaffold"] {
    border-color: #795548;
    background: rgba(121, 85, 72, 0.15);
  }
  .markup-link-popup[data-tag-type="Scaffold"]:hover,
  .markup-link[data-tag-type="Scaffold"]:hover {
    background: rgba(121, 85, 72, 0.35);
  }
  .markup-link-popup[data-tag-type="Scaffold"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Scaffold"] .tag-title.dimension-text {
    color: #4E342E;
  }
  .markup-link[data-tag-type="Scaffold"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(121, 85, 72, 0.4);
  }

  /* Tag Type Colors - Structural (red) */
  .markup-link-popup[data-tag-type="Structural"],
  .markup-link[data-tag-type="Structural"] {
    border-color: #F44336;
    background: rgba(244, 67, 54, 0.15);
  }
  .markup-link-popup[data-tag-type="Structural"]:hover,
  .markup-link[data-tag-type="Structural"]:hover {
    background: rgba(244, 67, 54, 0.35);
  }
  .markup-link-popup[data-tag-type="Structural"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Structural"] .tag-title.dimension-text {
    color: #C62828;
  }
  .markup-link[data-tag-type="Structural"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(244, 67, 54, 0.4);
  }

  /* Tag Type Colors - Anomaly (deep-orange) */
  .markup-link-popup[data-tag-type="Anomaly"],
  .markup-link[data-tag-type="Anomaly"] {
    border-color: #FF5722;
    background: rgba(255, 87, 34, 0.15);
  }
  .markup-link-popup[data-tag-type="Anomaly"]:hover,
  .markup-link[data-tag-type="Anomaly"]:hover {
    background: rgba(255, 87, 34, 0.35);
  }
  .markup-link-popup[data-tag-type="Anomaly"] .tag-title.dimension-text,
  .markup-link[data-tag-type="Anomaly"] .tag-title.dimension-text {
    color: #BF360C;
  }
  .markup-link[data-tag-type="Anomaly"].highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(255, 87, 34, 0.4);
  }

  .markup-link.selected-markup-link {
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.15);
  }

  .markup-link.highlighted-markup-link {
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.2);
    animation: pulse 1.5s infinite;
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }

  .markup-link-popup .tag-title.dimension-text,
  .markup-link .tag-title.dimension-text {
    flex-grow: 0;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 700;
    text-align: center;
    color: #424242;
    background: transparent;
  }

  /* ================================
     UTILITY CLASSES
     ================================ */
  .hidden {
    display: none !important;
  }

  .vertical-center {
    margin: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }

  .disable-select {
    user-select: none;
  }

  /* ================================
     SCROLLBAR STYLING
     ================================ */
  .panel-content-scroll::-webkit-scrollbar,
  .collapsible-panels-container::-webkit-scrollbar {
    width: 6px;
  }

  .panel-content-scroll::-webkit-scrollbar-track,
  .collapsible-panels-container::-webkit-scrollbar-track {
    background: transparent;
  }

  .panel-content-scroll::-webkit-scrollbar-thumb,
  .collapsible-panels-container::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }

  .panel-content-scroll::-webkit-scrollbar-thumb:hover,
  .collapsible-panels-container::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.25);
  }

  /* ================================
     LEGACY SUPPORT (KEEPING OLD CLASSES)
     ================================ */
  .markups-popup-underlay.shaded {
    background: black;
    opacity: 30%;
    pointer-events: none;
  }

  .left-markup-menu {
    flex-grow: 1;
    display: flex;
    flex-direction: row;
  }

  .justify-content-flex-start {
    justify-content: flex-start !important;
  }
</style>
